export interface StringOperators {
  in?: string[];
  nin?: string[];
}

export interface NumberOperators {
  in?: number[];
  nin?: number[];
  gt?: number;
  gte?: number;
  lt?: number;
  lte?: number;
}

export interface DateOperators {
  gt?: string;
  gte?: string;
  lt?: string;
  lte?: string;
}

export interface EnvironmentWhereOptions {
  name?: string;
  config?: object;
}

export enum OrderDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface EnvironmentOrderOptions {
  name?: OrderDirection;
  config?: OrderDirection;
}

export interface EnvironmentPagingFilterOptions {
  select?: ('name' | 'config')[];
  where?: EnvironmentWhereOptions;
  order?: EnvironmentOrderOptions;
  skip?: number;
  take?: number;
}

export interface Environment {
  name: string;
  config: object;
  createdAt?: string;
  updatedAt?: string;
}

export interface EnvironmentConfigField {
  id?: number;
  name: string;
  type: 'number' | 'string' | 'boolean' | 'credentials-url';
  required?: boolean;
  dataType: string;
  options: { group: string; dependency: string };
}

export interface EnvironmentPagingResult {
  total: number;
  skip: number;
  take: number;
  data: Environment[];
}

export interface ClientConfigResponseDto {
  googleClientId: string;
  appIcon?: string;
}

export enum ConfigDataType {
  String = 'string',
  Url = 'url',
  Number = 'number',
  ListString = 'list-string',
  Boolean = 'boolean',
  Image = 'image',
  Select = 'select',
}

export interface SystemConfig {
  key: string;
  title: string;
  description?: string;
  group?: string;
  dataType?: ConfigDataType;
  configurable?: boolean;
  required?: boolean;
  value?: string;
  options?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface SystemConfigDto {
  key: string;
  value?: string;
}

export interface BlueprintProtoUpdateHistoryWhereOptions {
  id?: number;
  sha?: string;
  note?: string;
  status?: 'UPDATING' | 'SUCCESS' | 'FAILED' | 'TIMEOUT' | StringOperators;
  source?: 'GITLAB' | 'GITHUB' | StringOperators;

  /** Raw proto structure in JSON */
  proto?: string;
  userEmail?: string;
  userAvatar?: string;
}

export interface BlueprintProtoUpdateHistoryOrderOptions {
  id?: OrderDirection;
  sha?: OrderDirection;
  note?: OrderDirection;
  status?: OrderDirection;
  source?: OrderDirection;
  proto?: OrderDirection;
  userEmail?: OrderDirection;
  userAvatar?: OrderDirection;
}

export interface BlueprintProtoUpdateHistoryPagingFilterOptions {
  select?: ('id' | 'sha' | 'note' | 'status' | 'source' | 'proto' | 'userEmail' | 'userAvatar')[];
  where?: BlueprintProtoUpdateHistoryWhereOptions;
  order?: BlueprintProtoUpdateHistoryOrderOptions;
  skip?: number;
  take?: number;
}

export interface BlueprintProtoUpdateHistory {
  id?: number;
  sha: string;
  note?: string;
  status: 'UPDATING' | 'SUCCESS' | 'FAILED' | 'TIMEOUT';
  source: 'GITLAB' | 'GITHUB';

  /** Raw proto structure in JSON */
  proto?: string;
  userEmail: string;
  userAvatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BlueprintProtoUpdateHistoryPagingResult {
  total: number;
  skip: number;
  take: number;
  data: BlueprintProtoUpdateHistory[];
}

export interface VariantSetting {
  name?: string;
  key: string;
}

export interface FieldReference {
  type: string;
  valueField: string;
  displayField?: string;
  allowInput?: boolean;
}

export enum FieldDataType {
  Date = 'date',
  Time = 'time',
  DateTime = 'date-time',
  Textarea = 'textarea',
  OdlFile = 'odl-file',
  Url = 'url',
  Rrule = 'rrule',
}

export interface FieldSettings {
  field: string;
  reference?: FieldReference;
  enumType?: FieldReference;
  required?: boolean;
  editable?: boolean;
  dataType?: FieldDataType;
  anyTypes?: string[];
  displayName?: string;
  isRSC?: boolean;
  allowedRSC?: string;
}

export interface BlueprintProtoWhereOptions {
  /** Indicate Blueprint has multiple variants */
  isVariant?: boolean;
  variants?: VariantSetting[];
  isSingle?: boolean;
  idFields?: string[];
  settings?: FieldSettings[];

  /** Indicate a protobuf message is disabled */
  enabled?: object;

  /** Protobuf message type name */
  name?: string;

  /** Protobuf message type */
  type?: string;
  shortName?: string;
  groupName?: string;

  /** Raw proto structure in JSON */
  proto?: object;

  /** Indicate a protobuf message represent an Enum */
  isEnum?: boolean;

  /** Indicate a protobuf message represent a Blueprint */
  isBlueprint?: boolean;
}

export interface BlueprintProtoOrderOptions {
  isVariant?: OrderDirection;
  variants?: OrderDirection;
  isSingle?: OrderDirection;
  idFields?: OrderDirection;
  settings?: OrderDirection;
  enabled?: OrderDirection;
  name?: OrderDirection;
  type?: OrderDirection;
  shortName?: OrderDirection;
  groupName?: OrderDirection;
  proto?: OrderDirection;
  isEnum?: OrderDirection;
  isBlueprint?: OrderDirection;
}

export interface BlueprintProtoPagingFilterOptions {
  select?: (
    | 'isVariant'
    | 'variants'
    | 'isSingle'
    | 'idFields'
    | 'settings'
    | 'enabled'
    | 'name'
    | 'type'
    | 'shortName'
    | 'groupName'
    | 'proto'
    | 'isEnum'
    | 'isBlueprint'
  )[];
  where?: BlueprintProtoWhereOptions;
  order?: BlueprintProtoOrderOptions;
  skip?: number;
  take?: number;
}

export interface BlueprintProto {
  /** Indicate Blueprint has multiple variants */
  isVariant?: boolean;
  variants?: VariantSetting[];
  isSingle?: boolean;
  idFields?: string[];
  settings?: FieldSettings[];

  /** Indicate a protobuf message is disabled */
  enabled?: object;

  /** Protobuf message type name */
  name: string;

  /** Protobuf message type */
  type: string;
  shortName: string;
  groupName?: string;

  /** Raw proto structure in JSON */
  proto: object;

  /** Indicate a protobuf message represent an Enum */
  isEnum?: boolean;

  /** Indicate a protobuf message represent a Blueprint */
  isBlueprint?: boolean;
  protoUpdateId?: number;
  syncVariant?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CloneBlueprintProtoDto {
  name: string;
  shortName: string;
  oldName: string;
}

export interface BlueprintDataVersionWhereOptions {
  name?: string;
  isBaseVersion?: boolean;
  baseVersion?: string;

  /** Indicate this version is not editable */
  isReadOnly?: boolean;

  /** Lock this data version with a proto version */
  protoUpdateId?: number;
  isCloning?: boolean;
  cloneVersion?: string;
  isFirstMoveChange?: boolean;
}

export interface BlueprintDataVersionOrderOptions {
  name?: OrderDirection;
  isBaseVersion?: OrderDirection;
  baseVersion?: OrderDirection;
  isReadOnly?: OrderDirection;
  protoUpdateId?: OrderDirection;
  isCloning?: OrderDirection;
  cloneVersion?: OrderDirection;
  isFirstMoveChange?: OrderDirection;
}

export interface BlueprintDataVersionPagingFilterOptions {
  select?: (
    | 'name'
    | 'isBaseVersion'
    | 'baseVersion'
    | 'isReadOnly'
    | 'protoUpdateId'
    | 'isCloning'
    | 'cloneVersion'
    | 'isFirstMoveChange'
  )[];
  where?: BlueprintDataVersionWhereOptions;
  order?: BlueprintDataVersionOrderOptions;
  skip?: number;
  take?: number;
}

export interface BlueprintDataVersion {
  name: string;
  isBaseVersion: boolean;
  baseVersion?: string;

  /** Indicate this version is not editable */
  isReadOnly?: boolean;

  /** Lock this data version with a proto version */
  protoUpdateId?: number;
  isCloning?: boolean;
  cloneVersion?: string;
  isFirstMoveChange?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export enum TaskStatus {
  PENDING = 'PENDING',
  DEPLOYING = 'DEPLOYING',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

export interface BlueprintDeployHistory {
  note?: string;

  /** Task status */
  status?: TaskStatus;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;

  /** Deploy environment */
  environment: string;

  /** Blueprint proto type name */
  types: string[];
  version: string;

  /** Timestamp of last change log record */
  lastChangedAt?: string;

  /** ID of last proto update record */
  lastProtoUpdateId?: number;
  forceUpdate?: boolean;

  /** Who made the change */
  userEmail?: string;
  userAvatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BlueprintDataVersionDetailDto {
  version: BlueprintDataVersion;
  protoUpdate?: BlueprintProtoUpdateHistory;
  deployments: BlueprintDeployHistory[];
}

export interface BlueprintDataVersionPagingResult {
  total: number;
  skip: number;
  take: number;
  data: BlueprintDataVersion[];
}

export interface BlueprintDeployHistoryWhereOptions {
  note?: string;
  status?: 'PENDING' | 'DEPLOYING' | 'SUCCESS' | 'FAIL' | 'TIMEOUT' | 'UNKNOWN' | StringOperators;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;

  /** Deploy environment */
  environment?: string;

  /** Blueprint proto type name */
  types?: string[];
  version?: string;
  lastChangedAt?: string | StringOperators;

  /** ID of last proto update record */
  lastProtoUpdateId?: number;
  forceUpdate?: boolean;

  /** Who made the change */
  userEmail?: string;
  userAvatar?: string;
}

export interface BlueprintDeployHistoryOrderOptions {
  note?: OrderDirection;
  status?: OrderDirection;
  message?: OrderDirection;
  scheduledAt?: OrderDirection;
  startTime?: OrderDirection;
  endTime?: OrderDirection;
  id?: OrderDirection;
  environment?: OrderDirection;
  types?: OrderDirection;
  version?: OrderDirection;
  lastChangedAt?: OrderDirection;
  lastProtoUpdateId?: OrderDirection;
  forceUpdate?: OrderDirection;
  userEmail?: OrderDirection;
  userAvatar?: OrderDirection;
}

export interface BlueprintDeployHistoryPagingFilterOptions {
  select?: (
    | 'note'
    | 'status'
    | 'message'
    | 'scheduledAt'
    | 'startTime'
    | 'endTime'
    | 'id'
    | 'environment'
    | 'types'
    | 'version'
    | 'lastChangedAt'
    | 'lastProtoUpdateId'
    | 'forceUpdate'
    | 'userEmail'
    | 'userAvatar'
  )[];
  where?: BlueprintDeployHistoryWhereOptions;
  order?: BlueprintDeployHistoryOrderOptions;
  skip?: number;
  take?: number;
}

export interface BlueprintDeployHistoryPagingResult {
  total: number;
  skip: number;
  take: number;
  data: BlueprintDeployHistory[];
}

export enum HealthStatus {
  Error = 'error',
  Ok = 'ok',
  ShuttingDown = 'shutting_down',
}

export enum ServiceHealthStatus {
  Up = 'up',
  Down = 'down',
}

export interface ServiceHealthStatusDto {
  status: ServiceHealthStatus;
  message?: string;
}

export interface ServiceHealthDto {
  database: ServiceHealthStatusDto;
}

export interface HealthDto {
  status: HealthStatus;

  /** Uptime in seconds */
  uptime: number;

  /** Is in maintenance mode? */
  maintenance: boolean;
  info: ServiceHealthDto;
  error: ServiceHealthDto;
}

export interface LoginRequestDto {
  googleTokenId: string;
}

export interface LoginResponseDto {
  token: string;
}

export interface GearUser {
  email: string;
  name: string;
  locked: number;
  avatar?: string;
  roles?: RoleMapping[];
  token?: string;
}

export interface RoleMapping {
  id: number;
  userEmail: string;
  role: string;
  environment?: string;
  user?: GearUser;
}

export interface RegisterRequestDto {
  email: string;
  name?: string;
  roles: RoleMapping[];
}

export enum UserRole {
  Authenticated = 'authenticated',
  Member = 'member',
  Ci = 'ci',
  System = 'system',
  DataEditor = 'data-editor',
  DataAdmin = 'data-admin',
  ProtoAdmin = 'proto-admin',
  Admin = 'admin',
  Deployer = 'deployer',
  CsViewer = 'cs-viewer',
  CsEditor = 'cs-editor',
  OdlEditor = 'odl-editor',
  OdlAdmin = 'odl-admin',
  AbsEditor = 'abs-editor',
  AbsAdmin = 'abs-admin',
}

export interface UserRoleMapping {
  role: UserRole;
  environments?: string[];
  isBaseRole?: string;
}

export interface MeDto {
  user: GearUser;
  roles: UserRoleMapping[];
}

export interface Role {
  name: string;
  title: string;
  description: string;
  isDefault?: boolean;
  environmentSpecific?: boolean;
  includes?: string[];
}

export interface GearUserWhereOptions {
  email?: string;
  name?: string;
  locked?: number;
  avatar?: string;
}

export interface GearUserOrderOptions {
  email?: OrderDirection;
  name?: OrderDirection;
  locked?: OrderDirection;
  avatar?: OrderDirection;
}

export interface GearUserPagingFilterOptions {
  select?: ('email' | 'name' | 'locked' | 'avatar')[];
  where?: GearUserWhereOptions;
  order?: GearUserOrderOptions;
  skip?: number;
  take?: number;
}

export interface GearUserPagingResult {
  total: number;
  skip: number;
  take: number;
  data: GearUser[];
}

export interface LogWhereOptions {
  id?: number;
  action?: string;
  avatar?: string;
  name?: string;
  note?: string;
  pageName?: string;
  updateBy?: string;
  updateOn?: string;
  changes?: string;
  environment?: string;
  clientVersion?: string;
}

export interface LogOrderOptions {
  id?: OrderDirection;
  action?: OrderDirection;
  avatar?: OrderDirection;
  name?: OrderDirection;
  note?: OrderDirection;
  pageName?: OrderDirection;
  updateBy?: OrderDirection;
  updateOn?: OrderDirection;
  changes?: OrderDirection;
  environment?: OrderDirection;
  clientVersion?: OrderDirection;
}

export interface LogPagingFilterOptions {
  select?: (
    | 'id'
    | 'action'
    | 'avatar'
    | 'name'
    | 'note'
    | 'pageName'
    | 'updateBy'
    | 'updateOn'
    | 'changes'
    | 'environment'
    | 'clientVersion'
  )[];
  where?: LogWhereOptions;
  order?: LogOrderOptions;
  skip?: number;
  take?: number;
}

export interface Log {
  id?: number;
  action: string;
  avatar: string;
  name?: string;
  note?: string;
  pageName: string;
  updateBy: string;
  updateOn: string;
  changes?: string;
  environment?: string;
  clientVersion?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LogPagingResult {
  total: number;
  skip: number;
  take: number;
  data: Log[];
}

export interface GitlabBodyDto {
  branch?: string;
  note?: string;
}

export enum BlueprintDataState {
  A = 'A',
  D = 'D',
  U = 'U',
}

export interface BlueprintData {
  objId?: string;

  /** Blueprint proto type name */
  type: string;

  /** Blueprint version */
  version: string;

  /** Blueprint data variant */
  variant?: string;
  data: Record<string, any>;

  /** Indicate state of the data in current version is (A)dded, (D)eleted or (U)pdated. Not set when untouched */
  state?: BlueprintDataState;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface BlueprintDataDiffNew {
  kind: string;
  path?: (string | number)[];
  rhs: any;
}

export interface BlueprintDataDiffDeleted {
  kind: string;
  path?: (string | number)[];
  lhs: any;
}

export interface BlueprintDataDiffEdit {
  kind: string;
  path?: (string | number)[];
  lhs: any;
  rhs: any;
}

export interface BlueprintDataDiffArray {
  kind: string;
  path?: (string | number)[];
  index: number;
  item:
    | BlueprintDataDiffNew
    | BlueprintDataDiffDeleted
    | BlueprintDataDiffEdit
    | BlueprintDataDiffArray;
}

export interface BlueprintDataChangeLog {
  id?: number;

  /** Modified blueprint object ID */
  objId: string;

  /** Protobuf type name */
  type: string;

  /** Blueprint version */
  version: string;

  /** Blueprint data variant */
  variant?: string;
  data: (
    | BlueprintDataDiffNew
    | BlueprintDataDiffDeleted
    | BlueprintDataDiffEdit
    | BlueprintDataDiffArray
  )[];

  /** Who made the change */
  userEmail: string;
  userAvatar?: string;
  deletedAt?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface BlueprintDataWithChangeLogsDto {
  data: BlueprintData[];
  baseData?: BlueprintData[];
  changeLogs?: BlueprintDataChangeLog[];
  deletedData?: BlueprintData[];
  messageError?: string;
}

export interface BlueprintDataStoreSingleDto {
  data: BlueprintData;
  generateRelicsExtras?: boolean;
}

export interface BlueprintDataStoreResultDto {
  data: BlueprintData;
  change?: BlueprintDataChangeLog;
}

export interface BlueprintDataStoreBatchDto {
  data: BlueprintData[];
  generateRelicsExtras?: boolean;
}

export interface DeleteMultipleBlueprintDataDto {
  objIds: string[];
}

export interface TaskQueue {
  note?: string;

  /** Task status */
  status: TaskStatus;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;
  name?: 'import' | 'MoveChanges' | 'DeleteBlueprintData';
  state?: string;
  returnValue?: object;
  createdAt?: string;
  updatedAt?: string;
}

export interface BlueprintDataMoveChangesDto {
  version: string;
  toVersion: string;
  type: string;
  objIds?: string[];
  overrideExistingData?: boolean;
}

export interface BlueprintDataMoveTypeChangesDto {
  type: string;
  objIds?: string[];
}

export interface BlueprintDataMoveMultipleChangesDto {
  version: string;
  toVersion: string;
  changes: BlueprintDataMoveTypeChangesDto[];
  overrideExistingData?: boolean;
}

export interface BlueprintDataCloneVariantDto {
  version: string;
  type: string;
  from: string;
  to: string;
  overrideAllData?: boolean;
}

export interface BlueprintDataChangeLogWhereOptions {
  id?: number;

  /** Modified blueprint object ID */
  objId?: string;

  /** Protobuf type name */
  type?: string;

  /** Blueprint version */
  version?: string;

  /** Blueprint data variant */
  variant?: string;
  data?: (
    | BlueprintDataDiffNew
    | BlueprintDataDiffDeleted
    | BlueprintDataDiffEdit
    | BlueprintDataDiffArray
  )[];

  /** Who made the change */
  userEmail?: string;
  userAvatar?: string;
}

export interface BlueprintDataChangeLogOrderOptions {
  id?: OrderDirection;
  objId?: OrderDirection;
  type?: OrderDirection;
  version?: OrderDirection;
  variant?: OrderDirection;
  data?: OrderDirection;
  userEmail?: OrderDirection;
  userAvatar?: OrderDirection;
}

export interface BlueprintDataChangeLogPagingFilterOptions {
  select?: (
    | 'id'
    | 'objId'
    | 'type'
    | 'version'
    | 'variant'
    | 'data'
    | 'userEmail'
    | 'userAvatar'
  )[];
  where?: BlueprintDataChangeLogWhereOptions;
  order?: BlueprintDataChangeLogOrderOptions;
  skip?: number;
  take?: number;
}

export interface BlueprintDataChangeLogPagingResult {
  total: number;
  skip: number;
  take: number;
  data: BlueprintDataChangeLog[];
}

export enum BlueprintDataFormat {
  Xlsx = 'xlsx',
  Csv = 'csv',
  Json = 'json',
  Zip = 'zip',
}

export interface BlueprintDataImportOptions {
  preview?: boolean;
  upsert?: boolean;
  reserveOrder?: boolean;
  overrideExistingData?: boolean;
  overrideAllData?: boolean;
  changes?: BlueprintDataMoveTypeChangesDto[];
}

export interface BlueprintDataImportDto {
  file: File;
  version: string;
  options?: BlueprintDataImportOptions;
  optionsBin?: File;
}

export interface BlueprintImportResultDto {
  data: BlueprintData[];
  baseData?: BlueprintData[];
  changeLogs?: BlueprintDataChangeLog[];
  deletedData?: BlueprintData[];
  messageError?: string;
  type: string;
  total: number;
}

export interface TaskQueueResponseDto {
  id: number;
  status: string;
  name: string;
  returnValue?: object;
}

export interface TaskQueueWhereOptions {
  note?: string;
  status?: 'PENDING' | 'DEPLOYING' | 'SUCCESS' | 'FAIL' | 'TIMEOUT' | 'UNKNOWN' | StringOperators;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;
  name?: string | StringOperators;
  state?: string;
  returnValue?: object;
}

export interface TaskQueueOrderOptions {
  note?: OrderDirection;
  status?: OrderDirection;
  message?: OrderDirection;
  scheduledAt?: OrderDirection;
  startTime?: OrderDirection;
  endTime?: OrderDirection;
  id?: OrderDirection;
  name?: OrderDirection;
  state?: OrderDirection;
  returnValue?: OrderDirection;
}

export interface TaskQueuePagingFilterOptions {
  select?: (
    | 'note'
    | 'status'
    | 'message'
    | 'scheduledAt'
    | 'startTime'
    | 'endTime'
    | 'id'
    | 'name'
    | 'state'
    | 'returnValue'
  )[];
  where?: TaskQueueWhereOptions;
  order?: TaskQueueOrderOptions;
  skip?: number;
  take?: number;
}

export interface TaskQueuePagingResult {
  total: number;
  skip: number;
  take: number;
  data: TaskQueue[];
}

export interface BlueprintDeployDto {
  environment: string;
  version: string;
  types?: string[];
  note?: string;
  forceUpdate?: boolean;
}

export interface AssetConfigWhereOptions {
  id?: number;
  clientVersion?: string;
  odl?: string;
  odb?: string;
  assetBundle?: string;
  platform?: 'Android' | 'iOS' | 'Windows' | 'MacOS' | StringOperators;
  environment?: string;
}

export interface AssetConfigOrderOptions {
  id?: OrderDirection;
  clientVersion?: OrderDirection;
  odl?: OrderDirection;
  odb?: OrderDirection;
  assetBundle?: OrderDirection;
  platform?: OrderDirection;
  environment?: OrderDirection;
}

export interface AssetConfigPagingFilterOptions {
  select?: ('id' | 'clientVersion' | 'odl' | 'odb' | 'assetBundle' | 'platform' | 'environment')[];
  where?: AssetConfigWhereOptions;
  order?: AssetConfigOrderOptions;
  skip?: number;
  take?: number;
}

export enum AssetPlatform {
  Android = 'Android',
  IOS = 'iOS',
  Windows = 'Windows',
  MacOS = 'MacOS',
}

export interface AssetConfig {
  id?: number;
  clientVersion: string;
  odl: string;
  odb: string;
  assetBundle: string;
  platform: AssetPlatform;
  environment: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssetConfigPagingResult {
  total: number;
  skip: number;
  take: number;
  data: AssetConfig[];
}

export interface GameConfigWhereOptions {
  id?: number;
  blueprintVersion?: string;
  environment?: string;
  platform?: 'Android' | 'iOS' | 'Windows' | 'MacOS' | StringOperators;
  clientVersion?: string;
  drbInAppId?: string;
  score?: number;
  keyRedis?: string;
  forcedUpdate?: boolean;
}

export interface GameConfigOrderOptions {
  id?: OrderDirection;
  blueprintVersion?: OrderDirection;
  environment?: OrderDirection;
  platform?: OrderDirection;
  clientVersion?: OrderDirection;
  drbInAppId?: OrderDirection;
  score?: OrderDirection;
  keyRedis?: OrderDirection;
  forcedUpdate?: OrderDirection;
}

export interface GameConfigPagingFilterOptions {
  select?: (
    | 'id'
    | 'blueprintVersion'
    | 'environment'
    | 'platform'
    | 'clientVersion'
    | 'drbInAppId'
    | 'score'
    | 'keyRedis'
    | 'forcedUpdate'
  )[];
  where?: GameConfigWhereOptions;
  order?: GameConfigOrderOptions;
  skip?: number;
  take?: number;
}

export interface GameConfig {
  id?: number;
  blueprintVersion: string;
  environment: string;
  platform: AssetPlatform;
  clientVersion: string;
  drbInAppId?: string;
  score?: number;
  keyRedis?: string;
  forcedUpdate?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface GameConfigPagingResult {
  total: number;
  skip: number;
  take: number;
  data: GameConfig[];
}

export interface MaintainItem {
  start: string;
  end: string;
}

export interface MaintainConfigWhereOptions {
  id?: number;
  clientVersion?: string;
  platform?: 'Android' | 'iOS' | 'Windows' | 'MacOS' | StringOperators;
  environment?: string;
  global?: MaintainItem;
  features?: object;
  messages?: any[];
}

export interface MaintainConfigOrderOptions {
  id?: OrderDirection;
  clientVersion?: OrderDirection;
  platform?: OrderDirection;
  environment?: OrderDirection;
  global?: OrderDirection;
  features?: OrderDirection;
  messages?: OrderDirection;
}

export interface MaintainConfigPagingFilterOptions {
  select?: (
    | 'id'
    | 'clientVersion'
    | 'platform'
    | 'environment'
    | 'global'
    | 'features'
    | 'messages'
  )[];
  where?: MaintainConfigWhereOptions;
  order?: MaintainConfigOrderOptions;
  skip?: number;
  take?: number;
}

export interface MaintainConfig {
  id?: number;
  clientVersion: string;
  platform: AssetPlatform;
  environment: string;
  global: MaintainItem;
  features: object;
  messages: any[];
  createdAt?: string;
  updatedAt?: string;
}

export interface MaintainConfigPagingResult {
  total: number;
  skip: number;
  take: number;
  data: MaintainConfig[];
}

export interface MessageDto {
  language: string;
  update_title: string;
  message: string;
  upgrade: string;
  support: string;
  userid: string;
  ios_url: string;
  android_url: string;
  steam_url: string;
  epic_url: string;
}

export interface UpdateMessageControllerDto {
  environment: string;
  type: string;
  messages: MessageDto[];
}

export interface GenerateTokenDto {
  expireDate?: string;
}

export interface GenerateTokenResponseDto {
  token: string;
}

export interface UpdatePidDto {
  pid?: number;
}

export interface BaseCurrencyBlueprint {
  id: string;
  capacity: number;
}

export interface BaseRbeTaskSegmentBlueprint {
  score: number;
}

export interface BaseRbeTaskBlueprint {
  id: string;
  reward: { $key?: string; $val?: number }[];
  segment: BaseRbeTaskSegmentBlueprint[];
}

export interface BaseRbeCompetitiveBlueprint {
  reward: string;
  segmentSize: number;
}

export interface BaseRbeEventBlueprint {
  id: string;
  task: string[];
  competitive?: BaseRbeCompetitiveBlueprint;
}

export interface Currency {
  currency: string;
  amount: number;
  update: number;
}

export interface RbeTaskDataDto {
  id: string;
  progress: number;
  completed: number;
  reward: number;
}

export interface RbeCompetitiveData {
  score: number;
  rank: number;
  leaderboard: string;
  reward: number;
}

export interface RbeEventDataDto {
  id: string;
  tasks: RbeTaskDataDto[];
  completed: number;
  start: number;
  end: number;
  competitive: RbeCompetitiveData;
  joinedAt: number;
}

export interface RbeDto {
  achievements: object;
  events: RbeEventDataDto[];
}

export enum PlayerDataSource {
  Redis = 'redis',
  Dynamo = 'dynamo',
}

export interface CurrencyDto {
  currency: string;
  amount: number;
  update: number;
}

export interface PlayerResourceDto {
  currencies: CurrencyDto[];
}

export interface ShopPackageDto {
  id: string;
  purchased: number;
  start: number;
}

export interface ShopGachaDto {
  id: string;
  pullMap: Record<string, number>;
  last: number;
}

export interface PlayerShopDto {
  shopMap: Record<string, ShopPackageDto>;
  gachaMap: Record<string, ShopGachaDto>;
}

export interface PlayerClanDto {
  id: string;
}

export interface PlayerInfoDto {
  name: string;
  pid: number;
  version: string;
  update: number;
  xp: string;
  ban: boolean;
}

export interface Player {
  id: string;
  playerId: string;
  pid: number;
  name: string;
  version: string;
  xp: number;
  ban: boolean;
  resources: Currency[];
  created: number;
  update: number;
  rbe?: RbeDto;
  source: PlayerDataSource;
  resource: PlayerResourceDto;
  devices: Record<string, any>;
  shop: PlayerShopDto;
  clan: PlayerClanDto;
  info: PlayerInfoDto;
}

export interface SocialAccount {
  account: string;
  connect: string;
}

export interface UpdatePlayerDto {
  name: string;
}

export interface AuditLogWhereOptions {
  id?: number;
  environment?: string;
  userEmail?: string;
  action?: string;
  playerId?: string;
  category?: string;
}

export interface AuditLogOrderOptions {
  id?: OrderDirection;
  environment?: OrderDirection;
  userEmail?: OrderDirection;
  action?: OrderDirection;
  playerId?: OrderDirection;
  category?: OrderDirection;
}

export interface AuditLogPagingFilterOptions {
  select?: ('id' | 'environment' | 'userEmail' | 'action' | 'playerId' | 'category')[];
  where?: AuditLogWhereOptions;
  order?: AuditLogOrderOptions;
  skip?: number;
  take?: number;
}

export interface InboxMessageExtraProto {
  name: string;
  proto: object;
}

export interface InboxMessageProtoDto {
  messageBlueprint: BlueprintProto;
  extraProtos?: InboxMessageExtraProto[];
}

export enum InboxVersion {
  V1 = 'v1',
  V2 = 'v2',
  V3 = 'v3',
}

export interface InboxDto {
  file: File;
  environment: string;
  data: string;
  playerId?: string;
  note?: string;
  scheduledAt?: string;
  blueprintVersion?: string;
}

export interface InboxHistory {
  note?: string;

  /** Task status */
  status?: TaskStatus;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;
  environment: string;
  version: 'v1' | 'v2' | 'v3';
  data: object;
  playerIds: string[];
  blueprintVersion?: string;
  userEmail: string;
  userAvatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InboxHistoryWhereOptions {
  note?: string;
  status?: 'PENDING' | 'DEPLOYING' | 'SUCCESS' | 'FAIL' | 'TIMEOUT' | 'UNKNOWN' | StringOperators;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;
  environment?: string;
  version?: string | StringOperators;
  data?: object;
  playerIds?: string[];
  blueprintVersion?: string;
  userEmail?: string;
  userAvatar?: string;
}

export interface InboxHistoryOrderOptions {
  note?: OrderDirection;
  status?: OrderDirection;
  message?: OrderDirection;
  scheduledAt?: OrderDirection;
  startTime?: OrderDirection;
  endTime?: OrderDirection;
  id?: OrderDirection;
  environment?: OrderDirection;
  version?: OrderDirection;
  data?: OrderDirection;
  playerIds?: OrderDirection;
  blueprintVersion?: OrderDirection;
  userEmail?: OrderDirection;
  userAvatar?: OrderDirection;
}

export interface InboxHistoryPagingFilterOptions {
  select?: (
    | 'note'
    | 'status'
    | 'message'
    | 'scheduledAt'
    | 'startTime'
    | 'endTime'
    | 'id'
    | 'environment'
    | 'version'
    | 'data'
    | 'playerIds'
    | 'blueprintVersion'
    | 'userEmail'
    | 'userAvatar'
  )[];
  where?: InboxHistoryWhereOptions;
  order?: InboxHistoryOrderOptions;
  skip?: number;
  take?: number;
}

export interface InboxHistoryPagingResult {
  total: number;
  skip: number;
  take: number;
  data: InboxHistory[];
}

export interface OdlFileWhereOptions {
  id?: string;
  name?: string;
  folder?: string;
  contentType?: string;
  isDir?: boolean;
  hasThumbnail?: boolean;
  url?: string;
  thumbnailUrl?: string;
  hash?: string;
  lastBuiltAt?: string;
  userEmail?: string;
}

export interface OdlFileOrderOptions {
  id?: OrderDirection;
  name?: OrderDirection;
  folder?: OrderDirection;
  contentType?: OrderDirection;
  isDir?: OrderDirection;
  hasThumbnail?: OrderDirection;
  url?: OrderDirection;
  thumbnailUrl?: OrderDirection;
  hash?: OrderDirection;
  lastBuiltAt?: OrderDirection;
  userEmail?: OrderDirection;
}

export interface OdlFilePagingFilterOptions {
  select?: (
    | 'id'
    | 'name'
    | 'folder'
    | 'contentType'
    | 'isDir'
    | 'hasThumbnail'
    | 'url'
    | 'thumbnailUrl'
    | 'hash'
    | 'lastBuiltAt'
    | 'userEmail'
  )[];
  where?: OdlFileWhereOptions;
  order?: OdlFileOrderOptions;
  skip?: number;
  take?: number;
}

export interface OdlFile {
  id: string;
  name: string;
  folder?: string;
  contentType?: string;
  isDir: boolean;
  hasThumbnail?: boolean;
  url?: string;
  thumbnailUrl?: string;
  hash?: string;
  lastBuiltAt?: string;
  userEmail?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FileUploadDto {
  file: File;
}

export interface DeleteFileDto {
  ids: string[];
}

export interface UrlDto {
  id: string;
  url: string;
}

export interface OdlFilePagingResult {
  total: number;
  skip: number;
  take: number;
  data: OdlFile[];
}

export interface OdlBuildHistoryWhereOptions {
  note?: string;
  status?: 'PENDING' | 'DEPLOYING' | 'SUCCESS' | 'FAIL' | 'TIMEOUT' | 'UNKNOWN' | StringOperators;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;

  /** File IDs list to build */
  fileIds?: string[];

  /** Jenkins build URL */
  buildUrl?: string;
  buildStatus?: 'QUEUING' | 'RUNNING' | 'ABORTED' | 'SUCCESS' | 'FAILURE' | StringOperators;
  userEmail?: string;
  userAvatar?: string;
}

export interface OdlBuildHistoryOrderOptions {
  note?: OrderDirection;
  status?: OrderDirection;
  message?: OrderDirection;
  scheduledAt?: OrderDirection;
  startTime?: OrderDirection;
  endTime?: OrderDirection;
  id?: OrderDirection;
  fileIds?: OrderDirection;
  buildUrl?: OrderDirection;
  buildStatus?: OrderDirection;
  userEmail?: OrderDirection;
  userAvatar?: OrderDirection;
}

export interface OdlBuildHistoryPagingFilterOptions {
  select?: (
    | 'note'
    | 'status'
    | 'message'
    | 'scheduledAt'
    | 'startTime'
    | 'endTime'
    | 'id'
    | 'fileIds'
    | 'buildUrl'
    | 'buildStatus'
    | 'userEmail'
    | 'userAvatar'
  )[];
  where?: OdlBuildHistoryWhereOptions;
  order?: OdlBuildHistoryOrderOptions;
  skip?: number;
  take?: number;
}

export enum OdlBuildStatus {
  QUEUING = 'QUEUING',
  RUNNING = 'RUNNING',
  ABORTED = 'ABORTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export interface OdlBuildHistory {
  note?: string;

  /** Task status */
  status?: TaskStatus;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number;

  /** File IDs list to build */
  fileIds: string[];

  /** Jenkins build URL */
  buildUrl?: string;
  buildStatus?: OdlBuildStatus;
  userEmail: string;
  userAvatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface OdlBuildHistoryPagingResult {
  total: number;
  skip: number;
  take: number;
  data: OdlBuildHistory[];
}

export interface OdlBuildInputDto {
  fileIds: string[];
}

export interface GetArenaSegmentDto {
  environment: string;
  blueprintVersion: string;
}

export interface ArenaSegmentDto {
  numberOfPlayers: number;
  rank: number;
  environment: string;
  blueprintVersion: string;
}

export interface ArenaSegmentHistory {
  note?: string;

  /** Task status */
  status?: TaskStatus;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id: number;
  environment: string;
  data: object;
  userEmail: string;
  taskType: string;
  blueprintVersion?: string;
  arenaSeasonId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ForcePlayerDto {
  playerId: string;
  segment: object;
  score: number;
  environment: object;
  blueprintVersion: string;
}

export interface ArenaSegmentHistoryWhereOptions {
  note?: string;
  status?: 'PENDING' | 'DEPLOYING' | 'SUCCESS' | 'FAIL' | 'TIMEOUT' | 'UNKNOWN' | StringOperators;
  message?: string;

  /** Schedule a task */
  scheduledAt?: string;
  startTime?: string;
  endTime?: string;
  id?: number | NumberOperators;
  environment?: string | StringOperators;
  data?: object;
  userEmail?: string | StringOperators;
  taskType?: string | StringOperators;
  blueprintVersion?: string | StringOperators;
  arenaSeasonId?: string | StringOperators;
}

export interface ArenaSegmentHistoryOrderOptions {
  note?: OrderDirection;
  status?: OrderDirection;
  message?: OrderDirection;
  scheduledAt?: OrderDirection;
  startTime?: OrderDirection;
  endTime?: OrderDirection;
  id?: OrderDirection;
  environment?: OrderDirection;
  data?: OrderDirection;
  userEmail?: OrderDirection;
  taskType?: OrderDirection;
  blueprintVersion?: OrderDirection;
  arenaSeasonId?: OrderDirection;
}

export interface ArenaSegmentHistoryPagingFilterOptions {
  select?: (
    | 'note'
    | 'status'
    | 'message'
    | 'scheduledAt'
    | 'startTime'
    | 'endTime'
    | 'id'
    | 'environment'
    | 'data'
    | 'userEmail'
    | 'taskType'
    | 'blueprintVersion'
    | 'arenaSeasonId'
  )[];
  where?: ArenaSegmentHistoryWhereOptions;
  order?: ArenaSegmentHistoryOrderOptions;
  skip?: number;
  take?: number;
}

export interface ArenaSegmentHistoryPagingResult {
  total: number;
  skip: number;
  take: number;
  data: ArenaSegmentHistory[];
}

export interface AddDevelopAccountDto {
  userId: string;
  environment: string;
}

export interface GetRecoveryMailDto {
  /** User ID */
  userId: string;

  /** Environment */
  environment: string;
}

export interface GetRecoveryMailResponseDto {
  /** Type */
  type: string;

  /** Account */
  account: string;

  /** Email */
  email?: string;
}

export interface GetRecoveryMailWithIAPResponseDto {
  playerInfo: PlayerInfoDto;
  accounts: GetRecoveryMailResponseDto[];
  IAPInfo: object[];
}

export interface GetRecoveryByUsernameDto {
  /** Username */
  username: string;

  /** Environment */
  environment: string;
}

export interface GetRecoveryByUsernameResponseDto {
  /** Clan ID */
  Clan_Id: string;

  /** Clan Name */
  Clan_Name: string;

  /** User ID */
  Id: string;

  /** Data */
  Data: string;
}

export interface ValidateRecoveryChangeItem {
  /** Account Type */
  type: string;

  /** Account */
  account: string;
}

export interface ValidateRecoveryDto {
  /** Current User ID */
  currentUserId: string;

  /** Environment */
  environment: string;

  /** Target User ID */
  targetUserId: string;

  /** Update Account */
  updateAccounts: ValidateRecoveryChangeItem[];
}

export enum LinkAccountType {
  Portal = 'portal',
  Ai = 'ai',
  St = 'st',
  Ga = 'ga',
}

export interface ValidateRecoveryResponseChangeItem {
  /** Account Type */
  type: LinkAccountType;

  /** Account */
  oldUserId: string;

  /** New Value */
  userId: string;

  /** Is Valid */
  isValid: boolean;

  /** Message */
  message: string;

  /** Account */
  account: string;
}

export interface ValidateRecoveryResponseDto {
  /** Is Valid */
  isValid: boolean;

  /** Message */
  message: string;

  /** Change */
  change: ValidateRecoveryResponseChangeItem[];
}

export interface ConfirmRecoveryDto {
  /** Current User ID */
  currentUserId: string;

  /** Environment */
  environment: string;

  /** Target User ID */
  targetUserId: string;

  /** Update Account */
  updateAccounts: ValidateRecoveryChangeItem[];
}

export interface GetDeviceIdDto {
  /** User ID */
  userId: string;

  /** Environment */
  environment: string;
}

export interface GetDeviceIdResponseDto {
  /** Device ID */
  deviceId?: string;
}

export interface GetCurrentProgressDto {
  /** Player ID */
  playerId: string;

  /** Environment */
  environment: string;

  /** Client version, used to resolve the matching proto version */
  clientVersion: string;
}

export interface GetCurrentProgressResponseDto {
  /** Current progress data, decoded from the stored Any-wrapped protobuf message */
  data?: object;
}

export interface GetClanWarProgressDto {
  /** Player ID */
  playerId: string;

  /** Environment */
  environment: string;

  /** Client version, used to resolve the matching proto version */
  clientVersion: string;
}

export interface GetClanWarProgressResponseDto {
  /** Current clan war progress data, decoded from the stored Any-wrapped protobuf message */
  data?: object;
}

export interface GetClanWarDefenseTeamDto {
  /** User ID */
  userId: string;

  /** Tournament ID */
  tournamentId: string;

  /** War ID */
  warId: string;

  /** Clan ID, reference only */
  clanId?: string;

  /** Season ID, reference only */
  seasonId?: string;

  /** Environment */
  environment: string;

  /** Client version, used to resolve the matching proto version */
  clientVersion: string;
}

export interface GetClanWarDefenseTeamResponseDto {
  /** User ID the defense team belongs to */
  userId: string;

  /** Clan ID as entered, reference only */
  clanId?: string;

  /** Season ID as entered, reference only */
  seasonId?: string;

  /** Tournament ID */
  tournamentId: string;

  /** War ID */
  warId: string;

  /** Defense team ID resolved from the clan war redis */
  defenseTeamId: string;

  /** Defense team lineup decoded from the stored Any-wrapped protobuf message */
  lineup?: object;
}

export interface GetGauntletProgressDto {
  /** Player ID */
  playerId: string;

  /** Environment */
  environment: string;

  /** Client version, used to resolve the matching proto version */
  clientVersion: string;
}

export interface GetGauntletProgressResponseDto {
  /** Current gauntlet progress data, decoded from the stored Any-wrapped protobuf message */
  data?: object;
}

export interface GetGauntletLineupDto {
  /** User ID */
  userId: string;

  /** Gauntlet round ID */
  roundId: string;

  /** Environment */
  environment: string;

  /** Client version, used to resolve the matching proto version */
  clientVersion: string;
}

export interface GauntletLineupPlayerDto {
  /** Player ID of the matchup member */
  userId: string;

  /** Lobby match status from playerMatchStatusMap, null when missing */
  status?: number | null;

  /** True when the player finished the lobby match (status = 3) */
  finished: boolean;

  /** Lineup data decoded from the stored Any-wrapped protobuf message */
  lineup?: object;
}

export interface GetGauntletLineupResponseDto {
  /** Current gauntlet season ID from the player profile */
  seasonId: string;

  /** Requested round ID */
  roundId: string;

  /** Matchup ID resolved from the player matchup history */
  matchupId: string;

  /** Random seed from the matchup info */
  randomSeed?: string;
  players: GauntletLineupPlayerDto[];
}

export interface GetGauntletPointDto {
  /** Environment name */
  environment: string;

  /** Gauntlet season id (key: wildcard:season:leaderboard:<seasonId>) */
  seasonId: string;

  /** User ids to look up */
  userIds: string[];
}

export interface GauntletPointRowDto {
  /** User id */
  userId: string;

  /** Gauntlet point; null when the user is not on the leaderboard */
  gauntletPoint?: number | null;
}

export interface ScanPlayersDto {
  /** Environment name */
  environment: string;

  /** DynamoDB FilterExpression */
  filterExpression?: string;

  /** DynamoDB ExpressionAttributeValues in raw typed AttributeValue format, e.g. {":lo":{"N":"50"},":hi":{"N":"100"}} */
  expressionAttributeValues?: object;

  /** DynamoDB ExpressionAttributeNames, e.g. {"#lvl":"Level"} */
  expressionAttributeNames?: object;

  /** Continuation cursor from a previous scan response (lastEvaluatedKey) */
  exclusiveStartKey?: object;

  /** Max number of items to scan (evaluate) in this single page, same as DynamoDB/AWS CLI --limit. When omitted, defaults to a fixed page size of 200. Mutually exclusive with limitMatch. */
  limit?: number;

  /** Instead of scanning a single page, keep paging automatically until this many distinct matched player ids have been found (or the table is exhausted). Mutually exclusive with limit. */
  limitMatch?: number;

  /** Output format: "txt" returns matched player ids only (default). "json" additionally returns a record per match (see jsonFields). */
  outputFormat?: 'txt' | 'json';

  /** Extra top-level attribute names to include on each record when outputFormat is "json", e.g. ["HighestDivisionId"]. A field missing on a given item is simply omitted from that record. Ignored when outputFormat is "txt". */
  jsonFields?: string[];
}

export interface ScanAllPlayersDto {
  /** Environment name */
  environment: string;

  /** Number of DynamoDB parallel-scan segments to split the table into (each scanned concurrently) - speeds up a large full-table scan. Leave empty, or set to 1, to scan sequentially as before. */
  segments?: number;

  /** DynamoDB FilterExpression */
  filterExpression?: string;

  /** DynamoDB ExpressionAttributeValues in raw typed AttributeValue format, e.g. {":lo":{"N":"50"},":hi":{"N":"100"}} */
  expressionAttributeValues?: object;

  /** DynamoDB ExpressionAttributeNames, e.g. {"#lvl":"Level"} */
  expressionAttributeNames?: object;

  /** Output format: "txt" writes matched player ids only (default). "json" writes a record per match (see jsonFields) as a JSON array. */
  outputFormat?: 'txt' | 'json';

  /** Extra top-level attribute names to include on each record when outputFormat is "json", e.g. ["HighestDivisionId"]. A field missing on a given item is simply omitted from that record. Ignored when outputFormat is "txt". */
  jsonFields?: string[];
}

export interface QueryResourceDto {
  /** Environment name */
  environment: string;

  /** DynamoDB table to query, PlayerId/ItemId/Amount schema. Defaults to "Resources" */
  table?: string;

  /** Table sort key (ItemId) to look up for every player, e.g. "Currency~GOLD" */
  currencyId: string;

  /** Player ids (table partition key, PlayerId) to look up */
  playerIds: string[];
}

export interface ScanHistoryItemDto {
  id: number;
  kind: 'scan' | 'load-more' | 'scan-all' | 'query-resource';

  /** TaskStatus - PENDING/DEPLOYING/SUCCESS/FAIL/... */
  status: string;
  startTime: string | null;
  endTime: string | null;

  /** The exact scan/scan-all request body used to start this job. Null if the job predates history tracking or failed before its params could be recorded. */
  params: object | null;
  scannedCount: number | null;
  matchedCount: number | null;

  /** Set only for a finished scan-all job - pass to GET /download/:fileName */
  fileName: string | null;

  /** Set only for a finished bounded scan/load-more/query-resource job */
  playerIds: string[] | null | null;

  /** Set only for a finished bounded scan/load-more job that ran with outputFormat "json", or a finished query-resource job */
  records: object[] | null | null;
  errorMessage: string | null;
}

export interface ScanHistoryResponseDto {
  total: number;
  skip: number;
  take: number;
  data: ScanHistoryItemDto[];
}

export interface InboxBatchPlayersDto {
  file: File;
  environment: string;
  data: string;
  playerId?: string;
  note?: string;
  scheduledAt?: string;
  blueprintVersion?: string;
}

export interface PlayerRewardItemDto {
  /** Item id, as configured in the Mailbox blueprint reward */
  itemId: string;
  quantity: number;
  /** com.alleylabs.game.type.RewardType numeric value */
  rewardType: number;
}

export interface PlayerMailRewardsDto {
  playerId: string;
  rewards: PlayerRewardItemDto[];
}

export interface InboxBatchRewardsDto {
  environment: string;
  blueprintVersion?: string;
  /** Mailbox blueprint template id (shared by every player sent) */
  mailTemplate: string;
  /** Unix epoch seconds (UTC), shared by every player sent */
  createdTime: number;
  extraData?: object;
  note?: string;
  players: PlayerMailRewardsDto[];
}

export type RequestParams = Omit<RequestInit, 'body' | 'method'> & {
  secure?: boolean;
};

export type Interceptors = {
  request?: ({ options }: { options: RequestParams }) => Promise<RequestParams> | RequestParams;
  response?: <D extends unknown = unknown, E extends unknown = unknown>({
    response,
  }: {
    response: HttpResponse<D, E>;
  }) => Promise<HttpResponse<D, E>>;
};

export type RequestQueryParamsType = Record<string | number, any>;

interface ApiConfig<SecurityDataType> {
  baseUrl?: string;
  baseApiParams?: RequestParams;
  securityWorker?: (securityData: SecurityDataType) => RequestParams;
  interceptors?: Interceptors;
}

export interface HttpResponse<D extends unknown, E extends unknown = unknown> extends Response {
  data: D | null;
  error: E | null;
}

enum BodyType {
  Json,
  FormData,
}

class HttpClient<SecurityDataType> {
  public baseUrl: string = '';
  private securityData: SecurityDataType = null as any;
  private securityWorker: null | ApiConfig<SecurityDataType>['securityWorker'] = null;
  private interceptors: Interceptors = {};

  private baseApiParams: RequestParams = {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  };

  constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
    Object.assign(this, apiConfig);
  }

  public setSecurityData = (data: SecurityDataType) => {
    this.securityData = data;
  };

  private addQueryParam(query: RequestQueryParamsType, key: string): string {
    let value: any = query[key];
    if (Array.isArray(value)) {
      value = value.join(',');
    } else if (typeof value === 'object') {
      value = JSON.stringify(value);
    }
    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }

  protected addQueryParams = (rawQuery?: RequestQueryParamsType): string => {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter((key) => typeof query[key] !== 'undefined');
    return keys.length ? `?${keys.map((key) => this.addQueryParam(query, key)).join('&')}` : '';
  };

  private bodyFormatters: Record<BodyType, (input: any) => any> = {
    [BodyType.Json]: JSON.stringify,
    [BodyType.FormData]: (input: any) =>
      Object.keys(input).reduce((data, key) => {
        data.append(key, input[key]);
        return data;
      }, new FormData()),
  };

  private mergeRequestOptions(
    params: RequestParams,
    securityParams?: RequestParams,
  ): RequestParams {
    const mergedParams = {
      ...this.baseApiParams,
      ...params,
      ...(securityParams || {}),
      headers: {
        ...(this.baseApiParams.headers || {}),
        ...(params.headers || {}),
        ...((securityParams && securityParams.headers) || {}),
      },
    };

    if (!(mergedParams.headers as any)['Content-Type']) {
      delete (mergedParams.headers as any)['Content-Type'];
    }
    return mergedParams;
  }

  private safeParseResponse = <T = any, E = any>(
    response: Response,
  ): Promise<HttpResponse<T, E>> => {
    const r = response as HttpResponse<T, E>;
    r.data = null;
    r.error = null;

    if (!response.headers.get('content-type')?.includes('application/json')) {
      return Promise.resolve(r);
    }

    return response
      .json()
      .then((data) => {
        if (r.ok) {
          r.data = data;
        } else {
          r.error = data;
        }
        return r;
      })
      .catch((e) => {
        r.error = e;
        return r;
      });
  };

  public request = async <T = any, E = any>(
    path: string,
    method: string,
    { secure, ...params }: RequestParams = {},
    body?: any,
    bodyType?: BodyType,
    secureByDefault?: boolean,
  ): Promise<HttpResponse<T, E>> => {
    const requestUrl = `${this.baseUrl}${path}`;
    const secureOptions =
      (secureByDefault || secure) && this.securityWorker
        ? this.securityWorker(this.securityData)
        : {};
    let options = this.mergeRequestOptions(params, secureOptions);
    if (this.interceptors.request) {
      options = await Promise.resolve(this.interceptors.request({ options }));
    }
    const requestOptions = {
      ...options,
      method,
      body: body ? this.bodyFormatters[bodyType || BodyType.Json](body) : null,
    };

    return fetch(requestUrl, requestOptions).then(async (response) => {
      let data = await this.safeParseResponse<T, E>(response);
      if (this.interceptors.response) {
        data = await this.interceptors.response<T, E>({ response: data });
      }
      if (!response.ok) throw data;
      return data;
    });
  };
}

/**
 * @title CBG Tool API
 * @version 0.0.1
 * Powered by GearGames
 */
export class Api<SecurityDataType = any> extends HttpClient<SecurityDataType> {
  api = {
    /**
     * @tags environment
     * @name EnvironmentController_create
     * @request POST:/api/environment
     * @secure
     */
    environmentControllerCreate: (data: Environment, params?: RequestParams) =>
      this.request<Environment, any>(`/api/environment`, 'POST', params, data, BodyType.Json, true),

    /**
     * @tags environment
     * @name EnvironmentController_list
     * @request GET:/api/environment
     * @secure
     */
    environmentControllerList: (
      query?: {
        select?: ('name' | 'config')[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<EnvironmentPagingResult, any>(
        `/api/environment${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags environment
     * @name EnvironmentController_update
     * @request PUT:/api/environment/{id}
     * @secure
     */
    environmentControllerUpdate: (id: string, data: Environment, params?: RequestParams) =>
      this.request<Environment, any>(
        `/api/environment/${id}`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags environment
     * @name EnvironmentController_findById
     * @request GET:/api/environment/{id}
     * @secure
     */
    environmentControllerFindById: (id: string, params?: RequestParams) =>
      this.request<Environment, any>(
        `/api/environment/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags environment
     * @name EnvironmentController_deleteById
     * @request DELETE:/api/environment/{id}
     * @secure
     */
    environmentControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/environment/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags environment
     * @name EnvironmentController_getABSEnvByName
     * @request GET:/api/environment/getABSEnvByName/{name}
     * @secure
     */
    environmentControllerGetAbsEnvByName: (name: string, params?: RequestParams) =>
      this.request<EnvironmentConfigField, any>(
        `/api/environment/getABSEnvByName/${name}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags environment
     * @name EnvironmentController_getConfigFields
     * @request GET:/api/environment/config-fields
     * @secure
     */
    environmentControllerGetConfigFields: (params?: RequestParams) =>
      this.request<EnvironmentConfigField[], any>(
        `/api/environment/config-fields`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags environment
     * @name EnvironmentController_getAll
     * @request GET:/api/environment/all
     * @secure
     */
    environmentControllerGetAll: (
      query?: { select?: ('name' | 'config')[]; order?: any; where?: any },
      params?: RequestParams,
    ) =>
      this.request<Environment[], any>(
        `/api/environment/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags environment
     * @name EnvironmentController_findOne
     * @request GET:/api/environment/findOne
     * @secure
     */
    environmentControllerFindOne: (
      query?: { select?: ('name' | 'config')[]; order?: any; where?: any },
      params?: RequestParams,
    ) =>
      this.request<Environment, any>(
        `/api/environment/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @name ABSToolController_getListBucketABSTool
     * @request GET:/api/abs-tool/bucket-abs-tool
     */
    absToolControllerGetListBucketAbsTool: (
      query: { envName?: string; absEnvId: number },
      params?: RequestParams,
    ) =>
      this.request<object, any>(
        `/api/abs-tool/bucket-abs-tool${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @name ABSToolController_getListMergeStrategyABSTool
     * @request GET:/api/abs-tool/merge-strategy-abs-tool
     */
    absToolControllerGetListMergeStrategyAbsTool: (
      query: { envName: string },
      params?: RequestParams,
    ) =>
      this.request<object, any>(
        `/api/abs-tool/merge-strategy-abs-tool${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @name ABSToolController_getListDrbABSTool
     * @request GET:/api/abs-tool/drb-abs-tool
     */
    absToolControllerGetListDrbAbsTool: (query?: { envName?: string }, params?: RequestParams) =>
      this.request<object, any>(
        `/api/abs-tool/drb-abs-tool${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags system-config
     * @name SystemConfigController_getPublicConfig
     * @request GET:/api/config/public.json
     */
    systemConfigControllerGetPublicConfig: (params?: RequestParams) =>
      this.request<ClientConfigResponseDto, any>(`/api/config/public.json`, 'GET', params),

    /**
     * @tags system-config
     * @name SystemConfigController_all
     * @request GET:/api/config/all
     * @secure
     */
    systemConfigControllerAll: (params?: RequestParams) =>
      this.request<SystemConfig[], any>(
        `/api/config/all`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags system-config
     * @name SystemConfigController_update
     * @request PUT:/api/config
     * @secure
     */
    systemConfigControllerUpdate: (data: SystemConfigDto, params?: RequestParams) =>
      this.request<SystemConfig, any>(`/api/config`, 'PUT', params, data, BodyType.Json, true),

    /**
     * @tags system-config
     * @name SystemConfigController_upsert
     * @request POST:/api/config
     * @secure
     */
    systemConfigControllerUpsert: (data: SystemConfigDto, params?: RequestParams) =>
      this.request<SystemConfig, any>(`/api/config`, 'POST', params, data, BodyType.Json, true),

    /**
     * @tags system-config
     * @name SystemConfigController_bulkUpdate
     * @request PUT:/api/config/bulk
     * @secure
     */
    systemConfigControllerBulkUpdate: (data: SystemConfigDto[], params?: RequestParams) =>
      this.request<SystemConfig[], any>(
        `/api/config/bulk`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto-update-history
     * @name BlueprintProtoUpdateHistoryController_list
     * @request GET:/api/blueprint-proto-update-history
     * @secure
     */
    blueprintProtoUpdateHistoryControllerList: (
      query?: {
        select?: (
          | 'id'
          | 'sha'
          | 'note'
          | 'status'
          | 'source'
          | 'proto'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintProtoUpdateHistoryPagingResult, any>(
        `/api/blueprint-proto-update-history${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto-update-history
     * @name BlueprintProtoUpdateHistoryController_getAll
     * @request GET:/api/blueprint-proto-update-history/all
     * @secure
     */
    blueprintProtoUpdateHistoryControllerGetAll: (
      query?: {
        select?: (
          | 'id'
          | 'sha'
          | 'note'
          | 'status'
          | 'source'
          | 'proto'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintProtoUpdateHistory[], any>(
        `/api/blueprint-proto-update-history/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto-update-history
     * @name BlueprintProtoUpdateHistoryController_findOne
     * @request GET:/api/blueprint-proto-update-history/findOne
     * @secure
     */
    blueprintProtoUpdateHistoryControllerFindOne: (
      query?: {
        select?: (
          | 'id'
          | 'sha'
          | 'note'
          | 'status'
          | 'source'
          | 'proto'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintProtoUpdateHistory, any>(
        `/api/blueprint-proto-update-history/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto-update-history
     * @name BlueprintProtoUpdateHistoryController_findById
     * @request GET:/api/blueprint-proto-update-history/{id}
     * @secure
     */
    blueprintProtoUpdateHistoryControllerFindById: (id: string, params?: RequestParams) =>
      this.request<BlueprintProtoUpdateHistory, any>(
        `/api/blueprint-proto-update-history/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto
     * @name BlueprintProtoController_getAll
     * @request GET:/api/blueprint-proto/all
     * @secure
     */
    blueprintProtoControllerGetAll: (query?: { updateId?: number }, params?: RequestParams) =>
      this.request<BlueprintProto[], any>(
        `/api/blueprint-proto/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto
     * @name BlueprintProtoController_update
     * @request PUT:/api/blueprint-proto/{id}
     * @secure
     */
    blueprintProtoControllerUpdate: (
      id: string,
      data: BlueprintProto,
      query?: { updateId?: number },
      params?: RequestParams,
    ) =>
      this.request<BlueprintProto, any>(
        `/api/blueprint-proto/${id}${this.addQueryParams(query)}`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto
     * @name BlueprintProtoController_deleteById
     * @request DELETE:/api/blueprint-proto/{id}
     * @secure
     */
    blueprintProtoControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/blueprint-proto/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto
     * @name BlueprintProtoController_cloneBlueprint
     * @request POST:/api/blueprint-proto/clone
     * @secure
     */
    blueprintProtoControllerCloneBlueprint: (
      data: CloneBlueprintProtoDto,
      query?: { updateId?: number },
      params?: RequestParams,
    ) =>
      this.request<BlueprintProto, any>(
        `/api/blueprint-proto/clone${this.addQueryParams(query)}`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto
     * @name BlueprintProtoController_getEnumProto
     * @request GET:/api/blueprint-proto/getEnumProto
     * @secure
     */
    blueprintProtoControllerGetEnumProto: (params?: RequestParams) =>
      this.request<BlueprintProto[], any>(
        `/api/blueprint-proto/getEnumProto`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto
     * @name BlueprintProtoController_getEnumProtoByName
     * @request GET:/api/blueprint-proto/getEnumProtoByName/{name}
     * @secure
     */
    blueprintProtoControllerGetEnumProtoByName: (name: string, params?: RequestParams) =>
      this.request<BlueprintProto, any>(
        `/api/blueprint-proto/getEnumProtoByName/${name}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-proto
     * @name BlueprintProtoController_create
     * @request POST:/api/blueprint-proto
     * @secure
     */
    blueprintProtoControllerCreate: (data: BlueprintProto, params?: RequestParams) =>
      this.request<BlueprintProto, any>(
        `/api/blueprint-proto`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_lock
     * @request PUT:/api/blueprint-data-version/{version}/lock
     * @secure
     */
    blueprintDataVersionControllerLock: (version: string, params?: RequestParams) =>
      this.request<BlueprintDataVersion, any>(
        `/api/blueprint-data-version/${version}/lock`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_unLock
     * @request PUT:/api/blueprint-data-version/{version}/unlock
     * @secure
     */
    blueprintDataVersionControllerUnLock: (version: string, params?: RequestParams) =>
      this.request<BlueprintDataVersion, any>(
        `/api/blueprint-data-version/${version}/unlock`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_detail
     * @request GET:/api/blueprint-data-version/{version}/detail
     * @secure
     */
    blueprintDataVersionControllerDetail: (version: string, params?: RequestParams) =>
      this.request<BlueprintDataVersionDetailDto, any>(
        `/api/blueprint-data-version/${version}/detail`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_list
     * @request GET:/api/blueprint-data-version
     * @secure
     */
    blueprintDataVersionControllerList: (
      query?: {
        select?: (
          | 'name'
          | 'isBaseVersion'
          | 'baseVersion'
          | 'isReadOnly'
          | 'protoUpdateId'
          | 'isCloning'
          | 'cloneVersion'
          | 'isFirstMoveChange'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataVersionPagingResult, any>(
        `/api/blueprint-data-version${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_create
     * @request POST:/api/blueprint-data-version
     * @secure
     */
    blueprintDataVersionControllerCreate: (data: BlueprintDataVersion, params?: RequestParams) =>
      this.request<BlueprintDataVersion, any>(
        `/api/blueprint-data-version`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_getAll
     * @request GET:/api/blueprint-data-version/all
     * @secure
     */
    blueprintDataVersionControllerGetAll: (
      query?: {
        select?: (
          | 'name'
          | 'isBaseVersion'
          | 'baseVersion'
          | 'isReadOnly'
          | 'protoUpdateId'
          | 'isCloning'
          | 'cloneVersion'
          | 'isFirstMoveChange'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataVersion[], any>(
        `/api/blueprint-data-version/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_findOne
     * @request GET:/api/blueprint-data-version/findOne
     * @secure
     */
    blueprintDataVersionControllerFindOne: (
      query?: {
        select?: (
          | 'name'
          | 'isBaseVersion'
          | 'baseVersion'
          | 'isReadOnly'
          | 'protoUpdateId'
          | 'isCloning'
          | 'cloneVersion'
          | 'isFirstMoveChange'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataVersion, any>(
        `/api/blueprint-data-version/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_findById
     * @request GET:/api/blueprint-data-version/{id}
     * @secure
     */
    blueprintDataVersionControllerFindById: (id: string, params?: RequestParams) =>
      this.request<BlueprintDataVersion, any>(
        `/api/blueprint-data-version/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_update
     * @request PUT:/api/blueprint-data-version/{id}
     * @secure
     */
    blueprintDataVersionControllerUpdate: (
      id: string,
      data: BlueprintDataVersion,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataVersion, any>(
        `/api/blueprint-data-version/${id}`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-version
     * @name BlueprintDataVersionController_deleteById
     * @request DELETE:/api/blueprint-data-version/{id}
     * @secure
     */
    blueprintDataVersionControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/blueprint-data-version/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy-history
     * @name BlueprintDeployHistoryController_getLastSucessfulDeploy
     * @request GET:/api/blueprint-deploy-history/last-success-deploy
     * @secure
     */
    blueprintDeployHistoryControllerGetLastSucessfulDeploy: (
      query?: { environment?: string; version?: string },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDeployHistory, any>(
        `/api/blueprint-deploy-history/last-success-deploy${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy-history
     * @name BlueprintDeployHistoryController_list
     * @request GET:/api/blueprint-deploy-history
     * @secure
     */
    blueprintDeployHistoryControllerList: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'types'
          | 'version'
          | 'lastChangedAt'
          | 'lastProtoUpdateId'
          | 'forceUpdate'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDeployHistoryPagingResult, any>(
        `/api/blueprint-deploy-history${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy-history
     * @name BlueprintDeployHistoryController_getAll
     * @request GET:/api/blueprint-deploy-history/all
     * @secure
     */
    blueprintDeployHistoryControllerGetAll: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'types'
          | 'version'
          | 'lastChangedAt'
          | 'lastProtoUpdateId'
          | 'forceUpdate'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDeployHistory[], any>(
        `/api/blueprint-deploy-history/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy-history
     * @name BlueprintDeployHistoryController_findOne
     * @request GET:/api/blueprint-deploy-history/findOne
     * @secure
     */
    blueprintDeployHistoryControllerFindOne: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'types'
          | 'version'
          | 'lastChangedAt'
          | 'lastProtoUpdateId'
          | 'forceUpdate'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDeployHistory, any>(
        `/api/blueprint-deploy-history/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy-history
     * @name BlueprintDeployHistoryController_findById
     * @request GET:/api/blueprint-deploy-history/{id}
     * @secure
     */
    blueprintDeployHistoryControllerFindById: (id: string, params?: RequestParams) =>
      this.request<BlueprintDeployHistory, any>(
        `/api/blueprint-deploy-history/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags health
     * @name HealthController_check
     * @request GET:/api/health
     */
    healthControllerCheck: (params?: RequestParams) =>
      this.request<
        HealthDto,
        {
          status?: string;
          info?: Record<string, { status?: string }>;
          error?: Record<string, { status?: string }>;
          details?: Record<string, { status?: string }>;
        }
      >(`/api/health`, 'GET', params),

    /**
     * @tags health
     * @name HealthController_startMaintenance
     * @request PUT:/api/health/maintenance/start
     * @secure
     */
    healthControllerStartMaintenance: (params?: RequestParams) =>
      this.request<any, any>(
        `/api/health/maintenance/start`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags health
     * @name HealthController_stopMaintenance
     * @request PUT:/api/health/maintenance/stop
     * @secure
     */
    healthControllerStopMaintenance: (params?: RequestParams) =>
      this.request<any, any>(
        `/api/health/maintenance/stop`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags auth
     * @name AuthController_login
     * @request POST:/api/auth/login
     */
    authControllerLogin: (data: LoginRequestDto, params?: RequestParams) =>
      this.request<LoginResponseDto, any>(`/api/auth/login`, 'POST', params, data),

    /**
     * @tags auth
     * @name AuthController_logout
     * @request POST:/api/auth/logout
     */
    authControllerLogout: (params?: RequestParams) =>
      this.request<string, any>(`/api/auth/logout`, 'POST', params),

    /**
     * @tags auth
     * @name AuthController_register
     * @request POST:/api/auth
     */
    authControllerRegister: (data: RegisterRequestDto, params?: RequestParams) =>
      this.request<GearUser, any>(`/api/auth`, 'POST', params, data),

    /**
     * @tags auth
     * @name AuthController_updateUser
     * @request PUT:/api/auth
     * @secure
     */
    authControllerUpdateUser: (data: GearUser, params?: RequestParams) =>
      this.request<GearUser, any>(`/api/auth`, 'PUT', params, data, BodyType.Json, true),

    /**
     * @tags auth
     * @name AuthController_me
     * @request GET:/api/auth/me
     * @secure
     */
    authControllerMe: (params?: RequestParams) =>
      this.request<MeDto, any>(`/api/auth/me`, 'GET', params, null, BodyType.Json, true),

    /**
     * @tags auth
     * @name AuthController_allRoles
     * @request GET:/api/auth/roles
     * @secure
     */
    authControllerAllRoles: (params?: RequestParams) =>
      this.request<Role[], any>(`/api/auth/roles`, 'GET', params, null, BodyType.Json, true),

    /**
     * @tags auth
     * @name AuthController_getRoles
     * @request GET:/api/auth/{email}/roles
     * @secure
     */
    authControllerGetRoles: (email: string, params?: RequestParams) =>
      this.request<UserRoleMapping[], any>(
        `/api/auth/${email}/roles`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags auth
     * @name AuthController_setRoles
     * @request PUT:/api/auth/{email}/roles
     * @secure
     */
    authControllerSetRoles: (email: string, data: UserRoleMapping[], params?: RequestParams) =>
      this.request<UserRoleMapping[], any>(
        `/api/auth/${email}/roles`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags users
     * @name GearUserController_list
     * @request GET:/api/users
     * @secure
     */
    gearUserControllerList: (
      query?: {
        select?: ('email' | 'name' | 'locked' | 'avatar')[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<GearUserPagingResult, any>(
        `/api/users${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags users
     * @name GearUserController_create
     * @request POST:/api/users
     * @secure
     */
    gearUserControllerCreate: (data: GearUser, params?: RequestParams) =>
      this.request<GearUser, any>(`/api/users`, 'POST', params, data, BodyType.Json, true),

    /**
     * @tags users
     * @name GearUserController_getAll
     * @request GET:/api/users/all
     * @secure
     */
    gearUserControllerGetAll: (
      query?: { select?: ('email' | 'name' | 'locked' | 'avatar')[]; order?: any; where?: any },
      params?: RequestParams,
    ) =>
      this.request<GearUser[], any>(
        `/api/users/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags users
     * @name GearUserController_findOne
     * @request GET:/api/users/findOne
     * @secure
     */
    gearUserControllerFindOne: (
      query?: { select?: ('email' | 'name' | 'locked' | 'avatar')[]; order?: any; where?: any },
      params?: RequestParams,
    ) =>
      this.request<GearUser, any>(
        `/api/users/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags users
     * @name GearUserController_findById
     * @request GET:/api/users/{id}
     * @secure
     */
    gearUserControllerFindById: (id: string, params?: RequestParams) =>
      this.request<GearUser, any>(`/api/users/${id}`, 'GET', params, null, BodyType.Json, true),

    /**
     * @tags users
     * @name GearUserController_update
     * @request PUT:/api/users/{id}
     * @secure
     */
    gearUserControllerUpdate: (id: string, data: GearUser, params?: RequestParams) =>
      this.request<GearUser, any>(`/api/users/${id}`, 'PUT', params, data, BodyType.Json, true),

    /**
     * @tags users
     * @name GearUserController_deleteById
     * @request DELETE:/api/users/{id}
     * @secure
     */
    gearUserControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(`/api/users/${id}`, 'DELETE', params, null, BodyType.Json, true),

    /**
     * @tags log
     * @name LogController_list
     * @request GET:/api/log
     * @secure
     */
    logControllerList: (
      query?: {
        select?: (
          | 'id'
          | 'action'
          | 'avatar'
          | 'name'
          | 'note'
          | 'pageName'
          | 'updateBy'
          | 'updateOn'
          | 'changes'
          | 'environment'
          | 'clientVersion'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<LogPagingResult, any>(
        `/api/log${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags log
     * @name LogController_create
     * @request POST:/api/log
     * @secure
     */
    logControllerCreate: (data: Log, params?: RequestParams) =>
      this.request<Log, any>(`/api/log`, 'POST', params, data, BodyType.Json, true),

    /**
     * @tags log
     * @name LogController_getAll
     * @request GET:/api/log/all
     * @secure
     */
    logControllerGetAll: (
      query?: {
        select?: (
          | 'id'
          | 'action'
          | 'avatar'
          | 'name'
          | 'note'
          | 'pageName'
          | 'updateBy'
          | 'updateOn'
          | 'changes'
          | 'environment'
          | 'clientVersion'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<Log[], any>(
        `/api/log/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags log
     * @name LogController_findOne
     * @request GET:/api/log/findOne
     * @secure
     */
    logControllerFindOne: (
      query?: {
        select?: (
          | 'id'
          | 'action'
          | 'avatar'
          | 'name'
          | 'note'
          | 'pageName'
          | 'updateBy'
          | 'updateOn'
          | 'changes'
          | 'environment'
          | 'clientVersion'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<Log, any>(
        `/api/log/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags log
     * @name LogController_findById
     * @request GET:/api/log/{id}
     * @secure
     */
    logControllerFindById: (id: string, params?: RequestParams) =>
      this.request<Log, any>(`/api/log/${id}`, 'GET', params, null, BodyType.Json, true),

    /**
     * @tags log
     * @name LogController_update
     * @request PUT:/api/log/{id}
     * @secure
     */
    logControllerUpdate: (id: string, data: Log, params?: RequestParams) =>
      this.request<Log, any>(`/api/log/${id}`, 'PUT', params, data, BodyType.Json, true),

    /**
     * @tags log
     * @name LogController_deleteById
     * @request DELETE:/api/log/{id}
     * @secure
     */
    logControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(`/api/log/${id}`, 'DELETE', params, null, BodyType.Json, true),

    /**
     * @tags proto/import
     * @name ProtoImportController_pull
     * @request POST:/api/proto/import/pull
     * @secure
     */
    protoImportControllerPull: (data: GitlabBodyDto, params?: RequestParams) =>
      this.request<BlueprintProtoUpdateHistory, any>(
        `/api/proto/import/pull`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_getAllData
     * @request GET:/api/blueprint-data/version/{version}/type/{type}
     * @secure
     */
    blueprintDataControllerGetAllData: (
      version: string,
      type: string,
      query?: { variant?: string; deployId?: number },
      params?: RequestParams,
    ) =>
      this.request<BlueprintData[], any>(
        `/api/blueprint-data/version/${version}/type/${type}${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_deleteMultipleData
     * @request DELETE:/api/blueprint-data/version/{version}/type/{type}
     * @secure
     */
    blueprintDataControllerDeleteMultipleData: (
      version: string,
      type: string,
      data: DeleteMultipleBlueprintDataDto,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog[], any>(
        `/api/blueprint-data/version/${version}/type/${type}`,
        'DELETE',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_getChangedTypesOfVersions
     * @request GET:/api/blueprint-data/compare/version/{version}
     * @secure
     */
    blueprintDataControllerGetChangedTypesOfVersions: (
      version: string,
      query?: {
        variant?: string;
        deployId?: number;
        compareVersion?: string;
        compareDeployId?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<object, any>(
        `/api/blueprint-data/compare/version/${version}${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_getDataAndCompare
     * @request GET:/api/blueprint-data/compare/version/{version}/type/{type}
     * @secure
     */
    blueprintDataControllerGetDataAndCompare: (
      version: string,
      type: string,
      query?: {
        variant?: string;
        deployId?: number;
        compareVersion?: string;
        compareDeployId?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataWithChangeLogsDto, any>(
        `/api/blueprint-data/compare/version/${version}/type/${type}${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_getAllDataAndCompareWithLastDeploy
     * @request GET:/api/blueprint-data/last-deploy/version/{version}/type/{type}
     * @secure
     */
    blueprintDataControllerGetAllDataAndCompareWithLastDeploy: (
      version: string,
      type: string,
      query: { environment: string; variant?: string },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataWithChangeLogsDto, any>(
        `/api/blueprint-data/last-deploy/version/${version}/type/${type}${this.addQueryParams(
          query,
        )}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_getData
     * @request GET:/api/blueprint-data/version/{version}/type/{type}/{objId}
     * @secure
     */
    blueprintDataControllerGetData: (
      version: string,
      type: string,
      objId: string,
      query?: { variant?: string; deployId?: number },
      params?: RequestParams,
    ) =>
      this.request<BlueprintData, any>(
        `/api/blueprint-data/version/${version}/type/${type}/${objId}${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_deleteData
     * @request DELETE:/api/blueprint-data/version/{version}/type/{type}/{objId}
     * @secure
     */
    blueprintDataControllerDeleteData: (
      version: string,
      type: string,
      objId: string,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog, any>(
        `/api/blueprint-data/version/${version}/type/${type}/${objId}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_storeData
     * @request POST:/api/blueprint-data
     * @secure
     */
    blueprintDataControllerStoreData: (data: BlueprintDataStoreSingleDto, params?: RequestParams) =>
      this.request<BlueprintDataStoreResultDto[], any>(
        `/api/blueprint-data`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_storeBatchData
     * @request POST:/api/blueprint-data/version/{version}/batch
     * @secure
     */
    blueprintDataControllerStoreBatchData: (
      version: string,
      data: BlueprintDataStoreBatchDto,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataStoreResultDto[], any>(
        `/api/blueprint-data/version/${version}/batch`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_deleteMultipleDataAsync
     * @request DELETE:/api/blueprint-data/async/version/{version}/type/{type}
     * @secure
     */
    blueprintDataControllerDeleteMultipleDataAsync: (
      version: string,
      type: string,
      data: DeleteMultipleBlueprintDataDto,
      params?: RequestParams,
    ) =>
      this.request<TaskQueue, any>(
        `/api/blueprint-data/async/version/${version}/type/${type}`,
        'DELETE',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_moveChangesToVersion
     * @request PUT:/api/blueprint-data/move
     * @secure
     */
    blueprintDataControllerMoveChangesToVersion: (
      data: BlueprintDataMoveChangesDto,
      params?: RequestParams,
    ) =>
      this.request<any, any>(`/api/blueprint-data/move`, 'PUT', params, data, BodyType.Json, true),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_moveMultipleChangesToVersion
     * @request PUT:/api/blueprint-data/move/multiple
     * @secure
     */
    blueprintDataControllerMoveMultipleChangesToVersion: (
      data: BlueprintDataMoveMultipleChangesDto,
      params?: RequestParams,
    ) =>
      this.request<any, any>(
        `/api/blueprint-data/move/multiple`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_moveMultipleChangesToVersionAsync
     * @request PUT:/api/blueprint-data/move/multiple_async
     * @secure
     */
    blueprintDataControllerMoveMultipleChangesToVersionAsync: (
      data: BlueprintDataMoveMultipleChangesDto,
      params?: RequestParams,
    ) =>
      this.request<TaskQueue, any>(
        `/api/blueprint-data/move/multiple_async`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_revertData
     * @request PUT:/api/blueprint-data/revert/version/{version}/type/{type}/{objId}
     * @secure
     */
    blueprintDataControllerRevertData: (
      version: string,
      type: string,
      objId: string,
      query: { lastChangedAt: string },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataStoreResultDto, any>(
        `/api/blueprint-data/revert/version/${version}/type/${type}/${objId}${this.addQueryParams(
          query,
        )}`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_cloneData
     * @request POST:/api/blueprint-data/clone/version/{version}/to/{toVersion}
     * @secure
     */
    blueprintDataControllerCloneData: (
      version: string,
      toVersion: string,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataVersion, any>(
        `/api/blueprint-data/clone/version/${version}/to/${toVersion}`,
        'POST',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_cloneVariant
     * @request POST:/api/blueprint-data/clone/variant
     * @secure
     */
    blueprintDataControllerCloneVariant: (
      data: BlueprintDataCloneVariantDto,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataWithChangeLogsDto, any>(
        `/api/blueprint-data/clone/variant`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data
     * @name BlueprintDataController_deleteByVersion
     * @request DELETE:/api/blueprint-data/{version}/delete
     * @secure
     */
    blueprintDataControllerDeleteByVersion: (version: string, params?: RequestParams) =>
      this.request<any, any>(
        `/api/blueprint-data/${version}/delete`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_getChangeLogsFromLastDeploy
     * @request GET:/api/blueprint-data-change-log/last-deploy
     * @secure
     */
    blueprintDataChangeLogControllerGetChangeLogsFromLastDeploy: (
      query: { environment: string; version: string; type: string; objId: string },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog[], any>(
        `/api/blueprint-data-change-log/last-deploy${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_getChangeLogsBetweenDeploys
     * @request GET:/api/blueprint-data-change-log/between-deploys
     * @secure
     */
    blueprintDataChangeLogControllerGetChangeLogsBetweenDeploys: (
      query: {
        version: string;
        type: string;
        objId: string;
        fromDeployId?: number;
        toDeployId?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog[], any>(
        `/api/blueprint-data-change-log/between-deploys${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_exportLocalizationToExcel
     * @request GET:/api/blueprint-data-change-log/export/version/{version}
     * @secure
     */
    blueprintDataChangeLogControllerExportLocalizationToExcel: (
      version: string,
      query?: { type?: string; from?: string; to?: string },
      params?: RequestParams,
    ) =>
      this.request<any, any>(
        `/api/blueprint-data-change-log/export/version/${version}${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_list
     * @request GET:/api/blueprint-data-change-log
     * @secure
     */
    blueprintDataChangeLogControllerList: (
      query?: {
        select?: (
          | 'id'
          | 'objId'
          | 'type'
          | 'version'
          | 'variant'
          | 'data'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLogPagingResult, any>(
        `/api/blueprint-data-change-log${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_create
     * @request POST:/api/blueprint-data-change-log
     * @secure
     */
    blueprintDataChangeLogControllerCreate: (
      data: BlueprintDataChangeLog,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog, any>(
        `/api/blueprint-data-change-log`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_getAll
     * @request GET:/api/blueprint-data-change-log/all
     * @secure
     */
    blueprintDataChangeLogControllerGetAll: (
      query?: {
        select?: (
          | 'id'
          | 'objId'
          | 'type'
          | 'version'
          | 'variant'
          | 'data'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog[], any>(
        `/api/blueprint-data-change-log/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_findOne
     * @request GET:/api/blueprint-data-change-log/findOne
     * @secure
     */
    blueprintDataChangeLogControllerFindOne: (
      query?: {
        select?: (
          | 'id'
          | 'objId'
          | 'type'
          | 'version'
          | 'variant'
          | 'data'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog, any>(
        `/api/blueprint-data-change-log/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_findById
     * @request GET:/api/blueprint-data-change-log/{id}
     * @secure
     */
    blueprintDataChangeLogControllerFindById: (id: string, params?: RequestParams) =>
      this.request<BlueprintDataChangeLog, any>(
        `/api/blueprint-data-change-log/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_update
     * @request PUT:/api/blueprint-data-change-log/{id}
     * @secure
     */
    blueprintDataChangeLogControllerUpdate: (
      id: string,
      data: BlueprintDataChangeLog,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDataChangeLog, any>(
        `/api/blueprint-data-change-log/${id}`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-change-log
     * @name BlueprintDataChangeLogController_deleteById
     * @request DELETE:/api/blueprint-data-change-log/{id}
     * @secure
     */
    blueprintDataChangeLogControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/blueprint-data-change-log/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-import-export
     * @name BlueprintDataImportController_exportToExcel
     * @request GET:/api/blueprint-data/export/version/{version}
     * @secure
     */
    blueprintDataImportControllerExportToExcel: (
      version: string,
      query?: { type?: string; deployId?: number; format?: BlueprintDataFormat },
      params?: RequestParams,
    ) =>
      this.request<any, any>(
        `/api/blueprint-data/export/version/${version}${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-import-export
     * @name BlueprintDataImportController_exportToJSON
     * @request GET:/api/blueprint-data/json/version/{version}
     * @secure
     */
    blueprintDataImportControllerExportToJson: (
      version: string,
      query?: { type?: string; deployId?: number },
      params?: RequestParams,
    ) =>
      this.request<any, any>(
        `/api/blueprint-data/json/version/${version}${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-data-import-export
     * @name BlueprintDataImportController_importFromExcel
     * @request POST:/api/blueprint-data/import
     * @secure
     */
    blueprintDataImportControllerImportFromExcel: (
      data: BlueprintDataImportDto,
      params?: RequestParams,
    ) =>
      this.request<BlueprintImportResultDto[], any>(
        `/api/blueprint-data/import`,
        'POST',
        params,
        data,
        BodyType.FormData,
        true,
      ),

    /**
     * @tags blueprint-data-import-export
     * @name BlueprintDataImportController_importFromExcelAsync
     * @request POST:/api/blueprint-data/import_async
     * @secure
     */
    blueprintDataImportControllerImportFromExcelAsync: (
      data: BlueprintDataImportDto,
      params?: RequestParams,
    ) =>
      this.request<TaskQueueResponseDto, any>(
        `/api/blueprint-data/import_async`,
        'POST',
        params,
        data,
        BodyType.FormData,
        true,
      ),

    /**
     * @tags task-queue
     * @name TaskQueueController_list
     * @request GET:/api/task-queue
     * @secure
     */
    taskQueueControllerList: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'name'
          | 'state'
          | 'returnValue'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<TaskQueuePagingResult, any>(
        `/api/task-queue${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags task-queue
     * @name TaskQueueController_getAll
     * @request GET:/api/task-queue/all
     * @secure
     */
    taskQueueControllerGetAll: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'name'
          | 'state'
          | 'returnValue'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<TaskQueue[], any>(
        `/api/task-queue/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags task-queue
     * @name TaskQueueController_findOne
     * @request GET:/api/task-queue/findOne
     * @secure
     */
    taskQueueControllerFindOne: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'name'
          | 'state'
          | 'returnValue'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<TaskQueue, any>(
        `/api/task-queue/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags task-queue
     * @name TaskQueueController_findById
     * @request GET:/api/task-queue/{id}
     * @secure
     */
    taskQueueControllerFindById: (id: string, params?: RequestParams) =>
      this.request<TaskQueue, any>(
        `/api/task-queue/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy
     * @name BlueprintDeployController_register
     * @request POST:/api/blueprint-deploy/register
     * @secure
     */
    blueprintDeployControllerRegister: (data: BlueprintDeployDto, params?: RequestParams) =>
      this.request<BlueprintDeployHistory, any>(
        `/api/blueprint-deploy/register`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy
     * @name BlueprintDeployController_getLastSucessfulDeployment
     * @request GET:/api/blueprint-deploy/last-success-deploy/{environment}/{version}
     * @secure
     */
    blueprintDeployControllerGetLastSucessfulDeployment: (
      environment: string,
      version: string,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDeployHistory, any>(
        `/api/blueprint-deploy/last-success-deploy/${environment}/${version}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy
     * @name BlueprintDeployController_exportToABSTool
     * @request POST:/api/blueprint-deploy/exportToABSTool/{envName}/{bucketId}/{mergeStrategy}
     * @secure
     */
    blueprintDeployControllerExportToAbsTool: (
      bucketId: string,
      envName: string,
      mergeStrategy: number,
      data: BlueprintDeployHistory,
      params?: RequestParams,
    ) =>
      this.request<BlueprintDeployHistory, any>(
        `/api/blueprint-deploy/exportToABSTool/${envName}/${bucketId}/${mergeStrategy}`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy
     * @name BlueprintDeployController_registerRedeploy
     * @request POST:/api/blueprint-deploy/{id}/redeploy
     * @secure
     */
    blueprintDeployControllerRegisterRedeploy: (id: number, params?: RequestParams) =>
      this.request<BlueprintDeployHistory, any>(
        `/api/blueprint-deploy/${id}/redeploy`,
        'POST',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy
     * @name BlueprintDeployController_getChangedTypesFromLastDeploy
     * @request GET:/api/blueprint-deploy/changed-types
     * @secure
     */
    blueprintDeployControllerGetChangedTypesFromLastDeploy: (
      query: { environment: string; version: string },
      params?: RequestParams,
    ) =>
      this.request<string[], any>(
        `/api/blueprint-deploy/changed-types${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy
     * @name BlueprintDeployController_getBlueprintDeployed
     * @request POST:/api/blueprint-deploy/{id}/data
     * @secure
     */
    blueprintDeployControllerGetBlueprintDeployed: (id: number, params?: RequestParams) =>
      this.request<object, any>(
        `/api/blueprint-deploy/${id}/data`,
        'POST',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags blueprint-deploy
     * @name BlueprintDeployController_getBinaryBlueprintDeployed
     * @request POST:/api/blueprint-deploy/{id}/data/binary
     * @secure
     */
    blueprintDeployControllerGetBinaryBlueprintDeployed: (id: number, params?: RequestParams) =>
      this.request<object, any>(
        `/api/blueprint-deploy/${id}/data/binary`,
        'POST',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags asset-config
     * @name AssetConfigController_upsert
     * @request POST:/api/asset-config/upsert
     * @secure
     */
    assetConfigControllerUpsert: (data: AssetConfig, params?: RequestParams) =>
      this.request<AssetConfig, any>(
        `/api/asset-config/upsert`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags asset-config
     * @name AssetConfigController_deleteById
     * @request DELETE:/api/asset-config/{id}
     * @secure
     */
    assetConfigControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/asset-config/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags asset-config
     * @name AssetConfigController_findById
     * @request GET:/api/asset-config/{id}
     * @secure
     */
    assetConfigControllerFindById: (id: string, params?: RequestParams) =>
      this.request<AssetConfig, any>(
        `/api/asset-config/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags asset-config
     * @name AssetConfigController_update
     * @request PUT:/api/asset-config/{id}
     * @secure
     */
    assetConfigControllerUpdate: (id: string, data: AssetConfig, params?: RequestParams) =>
      this.request<AssetConfig, any>(
        `/api/asset-config/${id}`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags asset-config
     * @name AssetConfigController_list
     * @request GET:/api/asset-config
     * @secure
     */
    assetConfigControllerList: (
      query?: {
        select?: (
          | 'id'
          | 'clientVersion'
          | 'odl'
          | 'odb'
          | 'assetBundle'
          | 'platform'
          | 'environment'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<AssetConfigPagingResult, any>(
        `/api/asset-config${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags asset-config
     * @name AssetConfigController_getAll
     * @request GET:/api/asset-config/all
     * @secure
     */
    assetConfigControllerGetAll: (
      query?: {
        select?: (
          | 'id'
          | 'clientVersion'
          | 'odl'
          | 'odb'
          | 'assetBundle'
          | 'platform'
          | 'environment'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<AssetConfig[], any>(
        `/api/asset-config/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags asset-config
     * @name AssetConfigController_findOne
     * @request GET:/api/asset-config/findOne
     * @secure
     */
    assetConfigControllerFindOne: (
      query?: {
        select?: (
          | 'id'
          | 'clientVersion'
          | 'odl'
          | 'odb'
          | 'assetBundle'
          | 'platform'
          | 'environment'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<AssetConfig, any>(
        `/api/asset-config/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_upsert
     * @request POST:/api/game-config/upsert
     * @secure
     */
    gameConfigControllerUpsert: (data: GameConfig, params?: RequestParams) =>
      this.request<GameConfig[], any>(
        `/api/game-config/upsert`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_forceUpdate
     * @request PUT:/api/game-config/{id}/force
     * @secure
     */
    gameConfigControllerForceUpdate: (id: string, params?: RequestParams) =>
      this.request<GameConfig, any>(
        `/api/game-config/${id}/force`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_unforceUpdate
     * @request PUT:/api/game-config/{id}/unforce
     * @secure
     */
    gameConfigControllerUnforceUpdate: (id: string, params?: RequestParams) =>
      this.request<GameConfig, any>(
        `/api/game-config/${id}/unforce`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_deleteById
     * @request DELETE:/api/game-config/{id}
     * @secure
     */
    gameConfigControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/game-config/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_findById
     * @request GET:/api/game-config/{id}
     * @secure
     */
    gameConfigControllerFindById: (id: string, params?: RequestParams) =>
      this.request<GameConfig, any>(
        `/api/game-config/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_list
     * @request GET:/api/game-config
     * @secure
     */
    gameConfigControllerList: (
      query?: {
        select?: (
          | 'id'
          | 'blueprintVersion'
          | 'environment'
          | 'platform'
          | 'clientVersion'
          | 'drbInAppId'
          | 'score'
          | 'keyRedis'
          | 'forcedUpdate'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<GameConfigPagingResult, any>(
        `/api/game-config${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_getAll
     * @request GET:/api/game-config/all
     * @secure
     */
    gameConfigControllerGetAll: (
      query?: {
        select?: (
          | 'id'
          | 'blueprintVersion'
          | 'environment'
          | 'platform'
          | 'clientVersion'
          | 'drbInAppId'
          | 'score'
          | 'keyRedis'
          | 'forcedUpdate'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<GameConfig[], any>(
        `/api/game-config/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags game-config
     * @name GameConfigController_findOne
     * @request GET:/api/game-config/findOne
     * @secure
     */
    gameConfigControllerFindOne: (
      query?: {
        select?: (
          | 'id'
          | 'blueprintVersion'
          | 'environment'
          | 'platform'
          | 'clientVersion'
          | 'drbInAppId'
          | 'score'
          | 'keyRedis'
          | 'forcedUpdate'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<GameConfig, any>(
        `/api/game-config/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags maintain-config
     * @name MaintainConfigController_upsert
     * @request POST:/api/maintain-config/upsert
     * @secure
     */
    maintainConfigControllerUpsert: (data: MaintainConfig, params?: RequestParams) =>
      this.request<MaintainConfig, any>(
        `/api/maintain-config/upsert`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags maintain-config
     * @name MaintainConfigController_deleteById
     * @request DELETE:/api/maintain-config/{id}
     * @secure
     */
    maintainConfigControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/maintain-config/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags maintain-config
     * @name MaintainConfigController_findById
     * @request GET:/api/maintain-config/{id}
     * @secure
     */
    maintainConfigControllerFindById: (id: string, params?: RequestParams) =>
      this.request<MaintainConfig, any>(
        `/api/maintain-config/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags maintain-config
     * @name MaintainConfigController_update
     * @request PUT:/api/maintain-config/{id}
     * @secure
     */
    maintainConfigControllerUpdate: (id: string, data: MaintainConfig, params?: RequestParams) =>
      this.request<MaintainConfig, any>(
        `/api/maintain-config/${id}`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags maintain-config
     * @name MaintainConfigController_list
     * @request GET:/api/maintain-config
     * @secure
     */
    maintainConfigControllerList: (
      query?: {
        select?: (
          | 'id'
          | 'clientVersion'
          | 'platform'
          | 'environment'
          | 'global'
          | 'features'
          | 'messages'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<MaintainConfigPagingResult, any>(
        `/api/maintain-config${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags maintain-config
     * @name MaintainConfigController_getAll
     * @request GET:/api/maintain-config/all
     * @secure
     */
    maintainConfigControllerGetAll: (
      query?: {
        select?: (
          | 'id'
          | 'clientVersion'
          | 'platform'
          | 'environment'
          | 'global'
          | 'features'
          | 'messages'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<MaintainConfig[], any>(
        `/api/maintain-config/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags maintain-config
     * @name MaintainConfigController_findOne
     * @request GET:/api/maintain-config/findOne
     * @secure
     */
    maintainConfigControllerFindOne: (
      query?: {
        select?: (
          | 'id'
          | 'clientVersion'
          | 'platform'
          | 'environment'
          | 'global'
          | 'features'
          | 'messages'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<MaintainConfig, any>(
        `/api/maintain-config/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags update-message
     * @name UpdateMessageController_updateMessage
     * @request POST:/api/update-message
     */
    updateMessageControllerUpdateMessage: (
      data: UpdateMessageControllerDto,
      params?: RequestParams,
    ) => this.request<any, any>(`/api/update-message`, 'POST', params, data),

    /**
     * @tags update-message
     * @name UpdateMessageController_getUpdateMessage
     * @request GET:/api/update-message
     */
    updateMessageControllerGetUpdateMessage: (
      query: { environment: string; type: string },
      params?: RequestParams,
    ) =>
      this.request<MessageDto[], any>(
        `/api/update-message${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags token
     * @name ApiTokenController_generate
     * @request POST:/api/token
     * @secure
     */
    apiTokenControllerGenerate: (data: GenerateTokenDto, params?: RequestParams) =>
      this.request<GenerateTokenResponseDto, any>(
        `/api/token`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_updatePidUser
     * @request PUT:/api/cstool/user/pid/{userId}
     */
    csUserControllerUpdatePidUser: (
      userId: string,
      query: { environment: string },
      data: UpdatePidDto,
      params?: RequestParams,
    ) =>
      this.request<Player, any>(
        `/api/cstool/user/pid/${userId}${this.addQueryParams(query)}`,
        'PUT',
        params,
        data,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_getUser
     * @request GET:/api/cstool/user/{userId}
     */
    csUserControllerGetUser: (
      userId: string,
      query: { environment: string },
      params?: RequestParams,
    ) =>
      this.request<Player, any>(
        `/api/cstool/user/${userId}${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_updateUser
     * @request PUT:/api/cstool/user/{userId}
     */
    csUserControllerUpdateUser: (
      userId: string,
      query: { environment: string },
      data: UpdatePlayerDto,
      params?: RequestParams,
    ) =>
      this.request<Player, any>(
        `/api/cstool/user/${userId}${this.addQueryParams(query)}`,
        'PUT',
        params,
        data,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_getCurrencies
     * @request GET:/api/cstool/user/{userId}/currencies
     */
    csUserControllerGetCurrencies: (
      userId: string,
      query: { environment: string },
      params?: RequestParams,
    ) =>
      this.request<BaseCurrencyBlueprint[], any>(
        `/api/cstool/user/${userId}/currencies${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_getSocialAccounts
     * @request GET:/api/cstool/user/{userId}/social-accounts
     */
    csUserControllerGetSocialAccounts: (
      userId: string,
      query: { environment: string },
      params?: RequestParams,
    ) =>
      this.request<SocialAccount[], any>(
        `/api/cstool/user/${userId}/social-accounts${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_addCurrency
     * @request POST:/api/cstool/user/{userId}/currency
     */
    csUserControllerAddCurrency: (
      userId: string,
      query: { environment: string },
      data: CurrencyDto,
      params?: RequestParams,
    ) =>
      this.request<CurrencyDto, any>(
        `/api/cstool/user/${userId}/currency${this.addQueryParams(query)}`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_updateCurrency
     * @request PUT:/api/cstool/user/{userId}/currency
     */
    csUserControllerUpdateCurrency: (
      userId: string,
      query: { environment: string },
      data: CurrencyDto,
      params?: RequestParams,
    ) =>
      this.request<CurrencyDto, any>(
        `/api/cstool/user/${userId}/currency${this.addQueryParams(query)}`,
        'PUT',
        params,
        data,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_removeCurrency
     * @request DELETE:/api/cstool/user/{userId}/currency
     */
    csUserControllerRemoveCurrency: (
      userId: string,
      query: { environment: string; currency: string },
      params?: RequestParams,
    ) =>
      this.request<any, any>(
        `/api/cstool/user/${userId}/currency${this.addQueryParams(query)}`,
        'DELETE',
        params,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_updateCurrencyV2
     * @request PUT:/api/cstool/user/{userId}/currencyV2
     */
    csUserControllerUpdateCurrencyV2: (
      userId: string,
      query: { environment: string },
      data: CurrencyDto,
      params?: RequestParams,
    ) =>
      this.request<CurrencyDto, any>(
        `/api/cstool/user/${userId}/currencyV2${this.addQueryParams(query)}`,
        'PUT',
        params,
        data,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_getBlueprintData
     * @request GET:/api/cstool/user/{userId}/blueprint/{type}
     */
    csUserControllerGetBlueprintData: (
      userId: string,
      type: string,
      query: { environment: string; variant?: string },
      params?: RequestParams,
    ) =>
      this.request<BlueprintData[], any>(
        `/api/cstool/user/${userId}/blueprint/${type}${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_removeSocialAccount
     * @request DELETE:/api/cstool/user/{userId}/connect
     */
    csUserControllerRemoveSocialAccount: (
      userId: string,
      query: { environment: string; connect: string },
      params?: RequestParams,
    ) =>
      this.request<any, any>(
        `/api/cstool/user/${userId}/connect${this.addQueryParams(query)}`,
        'DELETE',
        params,
      ),

    /**
     * @tags cstool/user
     * @name CsUserController_addSocialAccount
     * @request POST:/api/cstool/user/{userId}/social-account
     */
    csUserControllerAddSocialAccount: (
      userId: string,
      query: { environment: string },
      data: SocialAccount,
      params?: RequestParams,
    ) =>
      this.request<SocialAccount, any>(
        `/api/cstool/user/${userId}/social-account${this.addQueryParams(query)}`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags audit-log
     * @name AuditLogController_findByEnvironmentAndCreatedAtBetween
     * @request GET:/api/audit-log/find-between
     * @secure
     */
    auditLogControllerFindByEnvironmentAndCreatedAtBetween: (
      query?: {
        select?: ('id' | 'environment' | 'action')[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<any, any>(
        `/api/audit-log/find-between${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/inbox
     * @name CsInboxController_getMessageProto
     * @request GET:/api/cstool/inbox/proto/{version}
     * @secure
     */
    csInboxControllerGetMessageProto: (version: 'v1' | 'v2' | 'v3', params?: RequestParams) =>
      this.request<InboxMessageProtoDto, any>(
        `/api/cstool/inbox/proto/${version}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/inbox
     * @name CsInboxController_register
     * @request POST:/api/cstool/inbox/register/{version}
     * @secure
     */
    csInboxControllerRegister: (version: InboxVersion, data: InboxDto, params?: RequestParams) =>
      this.request<InboxHistory, any>(
        `/api/cstool/inbox/register/${version}`,
        'POST',
        params,
        data,
        BodyType.FormData,
        true,
      ),

    /**
     * @tags cstool/inbox
     * @name CsInboxController_redeploy
     * @request POST:/api/cstool/inbox/{id}/redeploy
     * @secure
     */
    csInboxControllerRedeploy: (id: number, params?: RequestParams) =>
      this.request<InboxHistory, any>(
        `/api/cstool/inbox/${id}/redeploy`,
        'POST',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/inbox/history
     * @name CsInboxHistoryController_list
     * @request GET:/api/cstool/inbox/history
     * @secure
     */
    csInboxHistoryControllerList: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'version'
          | 'data'
          | 'playerIds'
          | 'blueprintVersion'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<InboxHistoryPagingResult, any>(
        `/api/cstool/inbox/history${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/inbox/history
     * @name CsInboxHistoryController_getAll
     * @request GET:/api/cstool/inbox/history/all
     * @secure
     */
    csInboxHistoryControllerGetAll: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'version'
          | 'data'
          | 'playerIds'
          | 'blueprintVersion'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<InboxHistory[], any>(
        `/api/cstool/inbox/history/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/inbox/history
     * @name CsInboxHistoryController_findOne
     * @request GET:/api/cstool/inbox/history/findOne
     * @secure
     */
    csInboxHistoryControllerFindOne: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'version'
          | 'data'
          | 'playerIds'
          | 'blueprintVersion'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<InboxHistory, any>(
        `/api/cstool/inbox/history/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/inbox/history
     * @name CsInboxHistoryController_findById
     * @request GET:/api/cstool/inbox/history/{id}
     * @secure
     */
    csInboxHistoryControllerFindById: (id: string, params?: RequestParams) =>
      this.request<InboxHistory, any>(
        `/api/cstool/inbox/history/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_getAllFilesByFolder
     * @request GET:/api/odl/file/scan-dir
     * @secure
     */
    odlControllerGetAllFilesByFolder: (query?: { prefix?: string }, params?: RequestParams) =>
      this.request<OdlFile[], any>(
        `/api/odl/file/scan-dir${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_getAllUnbuildFiles
     * @request GET:/api/odl/file/unbuild
     * @secure
     */
    odlControllerGetAllUnbuildFiles: (query?: { prefix?: string }, params?: RequestParams) =>
      this.request<OdlFile[], any>(
        `/api/odl/file/unbuild${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_getAllFilesOfBuild
     * @request GET:/api/odl/file/build/{buildId}
     * @secure
     */
    odlControllerGetAllFilesOfBuild: (buildId: number, params?: RequestParams) =>
      this.request<OdlFile[], any>(
        `/api/odl/file/build/${buildId}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_createFolder
     * @request PUT:/api/odl/file/put/folder
     * @secure
     */
    odlControllerCreateFolder: (
      query: { filename: string; prefix?: string },
      params?: RequestParams,
    ) =>
      this.request<OdlFile, any>(
        `/api/odl/file/put/folder${this.addQueryParams(query)}`,
        'PUT',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_uploadFile
     * @request PUT:/api/odl/file/put
     * @secure
     */
    odlControllerUploadFile: (
      data: FileUploadDto,
      query?: { prefix?: string },
      params?: RequestParams,
    ) =>
      this.request<OdlFile, any>(
        `/api/odl/file/put${this.addQueryParams(query)}`,
        'PUT',
        params,
        data,
        BodyType.FormData,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_deleteFile
     * @request DELETE:/api/odl/file/s3
     * @secure
     */
    odlControllerDeleteFile: (data: DeleteFileDto, params?: RequestParams) =>
      this.request<any, any>(`/api/odl/file/s3`, 'DELETE', params, data, BodyType.Json, true),

    /**
     * @tags odl
     * @name OdlController_signGetUrl
     * @request GET:/api/odl/file/url
     * @secure
     */
    odlControllerSignGetUrl: (
      query: { filename: string; prefix?: string },
      params?: RequestParams,
    ) =>
      this.request<UrlDto, any>(
        `/api/odl/file/url${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_getSupportImageSize
     * @request GET:/api/odl/file/supportImageSize
     * @secure
     */
    odlControllerGetSupportImageSize: (params?: RequestParams) =>
      this.request<any, any>(
        `/api/odl/file/supportImageSize`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_list
     * @request GET:/api/odl/file
     * @secure
     */
    odlControllerList: (
      query?: {
        select?: (
          | 'id'
          | 'name'
          | 'folder'
          | 'contentType'
          | 'isDir'
          | 'hasThumbnail'
          | 'url'
          | 'thumbnailUrl'
          | 'hash'
          | 'lastBuiltAt'
          | 'userEmail'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<OdlFilePagingResult, any>(
        `/api/odl/file${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_getAll
     * @request GET:/api/odl/file/all
     * @secure
     */
    odlControllerGetAll: (
      query?: {
        select?: (
          | 'id'
          | 'name'
          | 'folder'
          | 'contentType'
          | 'isDir'
          | 'hasThumbnail'
          | 'url'
          | 'thumbnailUrl'
          | 'hash'
          | 'lastBuiltAt'
          | 'userEmail'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<OdlFile[], any>(
        `/api/odl/file/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_findOne
     * @request GET:/api/odl/file/findOne
     * @secure
     */
    odlControllerFindOne: (
      query?: {
        select?: (
          | 'id'
          | 'name'
          | 'folder'
          | 'contentType'
          | 'isDir'
          | 'hasThumbnail'
          | 'url'
          | 'thumbnailUrl'
          | 'hash'
          | 'lastBuiltAt'
          | 'userEmail'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<OdlFile, any>(
        `/api/odl/file/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl
     * @name OdlController_findById
     * @request GET:/api/odl/file/{id}
     * @secure
     */
    odlControllerFindById: (id: string, params?: RequestParams) =>
      this.request<OdlFile, any>(`/api/odl/file/${id}`, 'GET', params, null, BodyType.Json, true),

    /**
     * @tags odl-build-history
     * @name OdlBuildHistoryController_list
     * @request GET:/api/odl/build/history
     * @secure
     */
    odlBuildHistoryControllerList: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'fileIds'
          | 'buildUrl'
          | 'buildStatus'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<OdlBuildHistoryPagingResult, any>(
        `/api/odl/build/history${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl-build-history
     * @name OdlBuildHistoryController_getAll
     * @request GET:/api/odl/build/history/all
     * @secure
     */
    odlBuildHistoryControllerGetAll: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'fileIds'
          | 'buildUrl'
          | 'buildStatus'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<OdlBuildHistory[], any>(
        `/api/odl/build/history/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl-build-history
     * @name OdlBuildHistoryController_findOne
     * @request GET:/api/odl/build/history/findOne
     * @secure
     */
    odlBuildHistoryControllerFindOne: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'fileIds'
          | 'buildUrl'
          | 'buildStatus'
          | 'userEmail'
          | 'userAvatar'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<OdlBuildHistory, any>(
        `/api/odl/build/history/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl-build-history
     * @name OdlBuildHistoryController_findById
     * @request GET:/api/odl/build/history/{id}
     * @secure
     */
    odlBuildHistoryControllerFindById: (id: string, params?: RequestParams) =>
      this.request<OdlBuildHistory, any>(
        `/api/odl/build/history/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl-build
     * @name OdlBuildController_register
     * @request POST:/api/odl/build/register
     * @secure
     */
    odlBuildControllerRegister: (data: OdlBuildInputDto, params?: RequestParams) =>
      this.request<OdlBuildHistory, any>(
        `/api/odl/build/register`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @tags odl-build
     * @name OdlBuildController_registerRedeploy
     * @request POST:/api/odl/build/{id}/redeploy
     * @secure
     */
    odlBuildControllerRegisterRedeploy: (id: number, params?: RequestParams) =>
      this.request<OdlBuildHistory, any>(
        `/api/odl/build/${id}/redeploy`,
        'POST',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags arena-segment
     * @name ArenaSegmentController_getArenaSegments
     * @request POST:/api/arena-segment/server-get
     */
    arenaSegmentControllerGetArenaSegments: (data: GetArenaSegmentDto, params?: RequestParams) =>
      this.request<string[], any>(`/api/arena-segment/server-get`, 'POST', params, data),

    /**
     * @tags arena-segment
     * @name ArenaSegmentController_createArenaSegment
     * @request POST:/api/arena-segment/create
     */
    arenaSegmentControllerCreateArenaSegment: (data: ArenaSegmentDto, params?: RequestParams) =>
      this.request<ArenaSegmentHistory, any>(`/api/arena-segment/create`, 'POST', params, data),

    /**
     * @tags arena-segment
     * @name ArenaSegmentController_forcePlayer
     * @request POST:/api/arena-segment/force-player
     */
    arenaSegmentControllerForcePlayer: (data: ForcePlayerDto, params?: RequestParams) =>
      this.request<ArenaSegmentHistory, any>(
        `/api/arena-segment/force-player`,
        'POST',
        params,
        data,
      ),

    /**
     * @name ArenaSegmentHistoryController_list
     * @request GET:/api/arena-segment/history
     * @secure
     */
    arenaSegmentHistoryControllerList: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'data'
          | 'userEmail'
          | 'taskType'
          | 'blueprintVersion'
          | 'arenaSeasonId'
        )[];
        order?: any;
        where?: any;
        skip?: number;
        take?: number;
      },
      params?: RequestParams,
    ) =>
      this.request<ArenaSegmentHistoryPagingResult, any>(
        `/api/arena-segment/history${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @name ArenaSegmentHistoryController_create
     * @request POST:/api/arena-segment/history
     * @secure
     */
    arenaSegmentHistoryControllerCreate: (data: ArenaSegmentHistory, params?: RequestParams) =>
      this.request<ArenaSegmentHistory, any>(
        `/api/arena-segment/history`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @name ArenaSegmentHistoryController_getAll
     * @request GET:/api/arena-segment/history/all
     * @secure
     */
    arenaSegmentHistoryControllerGetAll: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'data'
          | 'userEmail'
          | 'taskType'
          | 'blueprintVersion'
          | 'arenaSeasonId'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<ArenaSegmentHistory[], any>(
        `/api/arena-segment/history/all${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @name ArenaSegmentHistoryController_findOne
     * @request GET:/api/arena-segment/history/findOne
     * @secure
     */
    arenaSegmentHistoryControllerFindOne: (
      query?: {
        select?: (
          | 'note'
          | 'status'
          | 'message'
          | 'scheduledAt'
          | 'startTime'
          | 'endTime'
          | 'id'
          | 'environment'
          | 'data'
          | 'userEmail'
          | 'taskType'
          | 'blueprintVersion'
          | 'arenaSeasonId'
        )[];
        order?: any;
        where?: any;
      },
      params?: RequestParams,
    ) =>
      this.request<ArenaSegmentHistory, any>(
        `/api/arena-segment/history/findOne${this.addQueryParams(query)}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @name ArenaSegmentHistoryController_findById
     * @request GET:/api/arena-segment/history/{id}
     * @secure
     */
    arenaSegmentHistoryControllerFindById: (id: string, params?: RequestParams) =>
      this.request<ArenaSegmentHistory, any>(
        `/api/arena-segment/history/${id}`,
        'GET',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @name ArenaSegmentHistoryController_update
     * @request PUT:/api/arena-segment/history/{id}
     * @secure
     */
    arenaSegmentHistoryControllerUpdate: (
      id: string,
      data: ArenaSegmentHistory,
      params?: RequestParams,
    ) =>
      this.request<ArenaSegmentHistory, any>(
        `/api/arena-segment/history/${id}`,
        'PUT',
        params,
        data,
        BodyType.Json,
        true,
      ),

    /**
     * @name ArenaSegmentHistoryController_deleteById
     * @request DELETE:/api/arena-segment/history/{id}
     * @secure
     */
    arenaSegmentHistoryControllerDeleteById: (id: string, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/arena-segment/history/${id}`,
        'DELETE',
        params,
        null,
        BodyType.Json,
        true,
      ),

    /**
     * @tags cstool/add-develop-account
     * @name DevelopAccountController_addDevelopAccount
     * @request POST:/api/cstool/add-develop-account
     */
    developAccountControllerAddDevelopAccount: (
      data: AddDevelopAccountDto,
      params?: RequestParams,
    ) =>
      this.request<AddDevelopAccountDto, any>(
        `/api/cstool/add-develop-account`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getRecoveryEmail
     * @request POST:/api/cstool/recovery-account/get-recovery-email
     */
    recoveryAccountControllerGetRecoveryEmail: (data: GetRecoveryMailDto, params?: RequestParams) =>
      this.request<GetRecoveryMailWithIAPResponseDto, any>(
        `/api/cstool/recovery-account/get-recovery-email`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getRecoveryByUsername
     * @request POST:/api/cstool/recovery-account/get-recovery-by-username
     */
    recoveryAccountControllerGetRecoveryByUsername: (
      data: GetRecoveryByUsernameDto,
      params?: RequestParams,
    ) =>
      this.request<GetRecoveryByUsernameResponseDto[], any>(
        `/api/cstool/recovery-account/get-recovery-by-username`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_streamByUsername
     * @request GET:/api/cstool/recovery-account/stream-by-username
     */
    recoveryAccountControllerStreamByUsername: (
      query: { username: string; environment: string },
      params?: RequestParams,
    ) =>
      this.request<object, any>(
        `/api/cstool/recovery-account/stream-by-username${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_validateRecovery
     * @request POST:/api/cstool/recovery-account/validate-recovery
     */
    recoveryAccountControllerValidateRecovery: (
      data: ValidateRecoveryDto,
      params?: RequestParams,
    ) =>
      this.request<ValidateRecoveryResponseDto, any>(
        `/api/cstool/recovery-account/validate-recovery`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_confirmRecovery
     * @request POST:/api/cstool/recovery-account/confirm-recovery
     */
    recoveryAccountControllerConfirmRecovery: (data: ConfirmRecoveryDto, params?: RequestParams) =>
      this.request<boolean, any>(
        `/api/cstool/recovery-account/confirm-recovery`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getDeviceId
     * @request POST:/api/cstool/recovery-account/get-device-id
     */
    recoveryAccountControllerGetDeviceId: (data: GetDeviceIdDto, params?: RequestParams) =>
      this.request<GetDeviceIdResponseDto, any>(
        `/api/cstool/recovery-account/get-device-id`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getCurrentProgress
     * @request POST:/api/cstool/recovery-account/get-current-progress
     */
    recoveryAccountControllerGetCurrentProgress: (
      data: GetCurrentProgressDto,
      params?: RequestParams,
    ) =>
      this.request<GetCurrentProgressResponseDto, any>(
        `/api/cstool/recovery-account/get-current-progress`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getClanWarProgress
     * @request POST:/api/cstool/recovery-account/get-clan-war-progress
     */
    recoveryAccountControllerGetClanWarProgress: (
      data: GetClanWarProgressDto,
      params?: RequestParams,
    ) =>
      this.request<GetClanWarProgressResponseDto, any>(
        `/api/cstool/recovery-account/get-clan-war-progress`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getClanWarDefenseTeam
     * @request POST:/api/cstool/recovery-account/get-clan-war-defense-team
     */
    recoveryAccountControllerGetClanWarDefenseTeam: (
      data: GetClanWarDefenseTeamDto,
      params?: RequestParams,
    ) =>
      this.request<GetClanWarDefenseTeamResponseDto, any>(
        `/api/cstool/recovery-account/get-clan-war-defense-team`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getGauntletProgress
     * @request POST:/api/cstool/recovery-account/get-gauntlet-progress
     */
    recoveryAccountControllerGetGauntletProgress: (
      data: GetGauntletProgressDto,
      params?: RequestParams,
    ) =>
      this.request<GetGauntletProgressResponseDto, any>(
        `/api/cstool/recovery-account/get-gauntlet-progress`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/recovery-account
     * @name RecoveryAccountController_getGauntletLineup
     * @request POST:/api/cstool/recovery-account/get-gauntlet-lineup
     */
    recoveryAccountControllerGetGauntletLineup: (
      data: GetGauntletLineupDto,
      params?: RequestParams,
    ) =>
      this.request<GetGauntletLineupResponseDto, any>(
        `/api/cstool/recovery-account/get-gauntlet-lineup`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/gauntlet-point
     * @name GauntletPointController_search
     * @request POST:/api/cstool/gauntlet-point/search
     */
    gauntletPointControllerSearch: (data: GetGauntletPointDto, params?: RequestParams) =>
      this.request<GauntletPointRowDto[], any>(
        `/api/cstool/gauntlet-point/search`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/gauntlet-point
     * @name GauntletPointController_exportExcel
     * @request POST:/api/cstool/gauntlet-point/export
     */
    gauntletPointControllerExportExcel: (data: GetGauntletPointDto, params?: RequestParams) =>
      this.request<any, any>(`/api/cstool/gauntlet-point/export`, 'POST', params, data),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_scan
     * @request POST:/api/cstool/scan-players/scan
     */
    scanPlayersControllerScan: (data: ScanPlayersDto, params?: RequestParams) =>
      this.request<TaskQueueResponseDto, any>(
        `/api/cstool/scan-players/scan`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_scanAll
     * @request POST:/api/cstool/scan-players/scan-all
     */
    scanPlayersControllerScanAll: (data: ScanAllPlayersDto, params?: RequestParams) =>
      this.request<TaskQueueResponseDto, any>(
        `/api/cstool/scan-players/scan-all`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_queryResource
     * @request POST:/api/cstool/scan-players/query-resource
     */
    scanPlayersControllerQueryResource: (data: QueryResourceDto, params?: RequestParams) =>
      this.request<TaskQueueResponseDto, any>(
        `/api/cstool/scan-players/query-resource`,
        'POST',
        params,
        data,
      ),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_getScanStatus
     * @request GET:/api/cstool/scan-players/status
     */
    scanPlayersControllerGetScanStatus: (params?: RequestParams) =>
      this.request<object, any>(`/api/cstool/scan-players/status`, 'GET', params),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_getTaskStatus
     * @request GET:/api/cstool/scan-players/task/{id}
     */
    scanPlayersControllerGetTaskStatus: (id: string, params?: RequestParams) =>
      this.request<TaskQueueResponseDto, any>(`/api/cstool/scan-players/task/${id}`, 'GET', params),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_getHistory
     * @request GET:/api/cstool/scan-players/history
     */
    scanPlayersControllerGetHistory: (
      query?: { skip?: string; take?: string },
      params?: RequestParams,
    ) =>
      this.request<ScanHistoryResponseDto, any>(
        `/api/cstool/scan-players/history${this.addQueryParams(query)}`,
        'GET',
        params,
      ),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_cancelScan
     * @request POST:/api/cstool/scan-players/cancel
     */
    scanPlayersControllerCancelScan: (params?: RequestParams) =>
      this.request<object, any>(`/api/cstool/scan-players/cancel`, 'POST', params),

    /**
     * @tags cstool/scan-players
     * @name ScanPlayersController_download
     * @request GET:/api/cstool/scan-players/download/{fileName}
     */
    scanPlayersControllerDownload: (fileName: string, params?: RequestParams) =>
      this.request<any, any>(`/api/cstool/scan-players/download/${fileName}`, 'GET', params),

    /**
     * @tags cstool/inbox-batch-players
     * @name InboxBatchPlayersController_register
     * @request POST:/api/cstool/inbox-batch-players/register/{version}
     * @secure
     */
    inboxBatchPlayersControllerRegister: (
      version: 'v1' | 'v2' | 'v3',
      data: InboxBatchPlayersDto,
      params?: RequestParams,
    ) =>
      this.request<InboxHistory[], any>(
        `/api/cstool/inbox-batch-players/register/${version}`,
        'POST',
        params,
        data,
        BodyType.FormData,
        true,
      ),

    /**
     * @tags cstool/inbox-batch-players
     * @name InboxBatchPlayersController_registerWithRewards
     * @request POST:/api/cstool/inbox-batch-players/register-with-rewards
     * @secure
     */
    inboxBatchPlayersControllerRegisterWithRewards: (
      data: InboxBatchRewardsDto,
      params?: RequestParams,
    ) =>
      this.request<InboxHistory[], any>(
        `/api/cstool/inbox-batch-players/register-with-rewards`,
        'POST',
        params,
        data,
        BodyType.Json,
        true,
      ),
  };
}
